import React, { useState, useCallback, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useNavBlocker from '../../hooks/useNavBlocker';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Helmet } from 'react-helmet-async';
import LoginPrompt from '../../Auth/LoginPrompt';
import ProUpgradeModal from '../../components/chat/ProUpgradeModal';
import Dialog from '../../components/common/Dialog';
import ModalActions from '../../components/common/ModalActions';
import SourcesButton from '../../components/chat/SourcesButton';
import FilePreCheckModal from '../../components/common/FilePreCheckModal';
import TeacherReferralShareButton from '../../components/invite/TeacherReferralShareButton';
import {
  deleteChatFile,
  preCheckFiles,
  uploadChatFile,
} from '../../api/chatApi';
// 📎 파일 첨부 한도 중앙 설정
import { getFileLimits, MAX_FILES_DEFAULT } from '../../constants/fileLimits';
import {
  applyAssessmentPlanToHwp,
  generateAssessmentPlanHwp,
  generateAssessmentPlan,
  refineAssessmentPlan,
  saveAssessmentPlan,
  detectSubjects,
  draftPlanFromStandards,
} from '../../api/assessmentPlanApi';
import { trackRiseFeatureAction, trackAssessmentPlanUxEvent } from '../../utils/eventTracker';
import { useToast } from '../../components/Toast';
import CountUp from 'react-countup';
import axios from 'axios';
import { getApiUrl } from '../../utils/apiConfig';
import AssessmentPlanHwpResult from './AssessmentPlanHwpResult';
import { buildCurriculumGroundingNotice } from './assessmentPlanGroundingNotice';
import SaenggibuAttachMenu from '../../components/saenggibu/SaenggibuAttachMenu';
import AchievementStandardEntry from '../../components/saenggibu/AchievementStandardEntry';
import AchievementStandardPicker from '../../components/saenggibu/AchievementStandardPicker';
import { appendStandardLines } from '../../components/saenggibu/achievementStandardPicker.utils';
import { getReferenceMaterial } from '../../api/referenceMaterialApi';
import { buildLibraryMirrorTitle, mirrorTextToLibrary } from '../../utils/libraryMirror';
import './AssessmentPlanPage.css';

const OVERVIEW_LABEL_MAP = {
  purpose: '목적', policy: '방침', precautions: '유의사항',
  direction_guidelines: '평가 방향', 'direction guidelines': '평가 방향',
  evaluation_ratio: '평가 비율', 'evaluation ratio': '평가 비율',
  summary: '요약', achievement_levels: '성취수준', 'achievement levels': '성취수준',
  ongoing_evaluation: '과정중심평가', diagnostic_activities: '진단활동',
  evaluation_results_utilization: '평가결과 활용', remote_learning_evaluation: '원격수업 평가',
  evaluation_notification: '평가 안내', evaluation_areas: '평가 영역',
  evaluation_methods_general: '평가 방법',
};

const formatOverviewValue = (val) => {
  if (typeof val === 'string') return val;
  if (Array.isArray(val)) {
    return val.map((v) => {
      if (typeof v === 'string') return `• ${v}`;
      if (typeof v === 'object' && v !== null) {
        const parts = Object.entries(v).filter(([, v2]) => typeof v2 === 'string').map(([k, v2]) => `${k}: ${v2}`);
        return parts.length ? `• ${parts.join(', ')}` : `• ${JSON.stringify(v)}`;
      }
      return `• ${String(v)}`;
    }).join('\n');
  }
  if (typeof val === 'object' && val !== null) {
    return Object.entries(val).map(([k, v]) => `${OVERVIEW_LABEL_MAP[k] || k.replace(/_/g, ' ')}: ${formatOverviewValue(v)}`).join('\n');
  }
  return String(val);
};

const safeOverviewText = (overview) => {
  if (!overview) return '';
  if (typeof overview === 'string') return overview;
  if (typeof overview === 'object' && !Array.isArray(overview)) {
    return Object.entries(overview)
      .map(([key, val]) => {
        const label = OVERVIEW_LABEL_MAP[key] || key.replace(/_/g, ' ');
        return `[${label}]\n${formatOverviewValue(val)}`;
      })
      .join('\n\n');
  }
  if (Array.isArray(overview)) return formatOverviewValue(overview);
  return String(overview);
};

const isHwpPlanFile = (file) => /\.(?:hwp|hwpx)$/i.test(file?.name || '');

const assessmentFileKey = (file) => [
  file?.name || '',
  Number(file?.size || 0),
  Number(file?.lastModified || 0),
].join(':');
const createAssessmentRequestId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `assessment-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};
const buildAssessmentPlanTextFromStructured = (structured) => {
  if (!structured) return '';
  const lines = [];
  if (structured.title) lines.push(structured.title);
  if (structured.overview) lines.push(safeOverviewText(structured.overview));
  lines.push('');
  (structured.items || []).forEach((item) => {
    lines.push(
      [
        item.name,
        item.type,
        item.points !== undefined ? `${item.points}점` : '',
        item.period,
        item.method,
      ].filter(Boolean).join(' | ')
    );
    if (item.achievement_standards) {
      lines.push(
        `성취기준: ${
          typeof item.achievement_standards === 'string'
            ? item.achievement_standards
            : JSON.stringify(item.achievement_standards)
        }`
      );
    }
    if (item.scoring_criteria?.length > 0) {
      lines.push('채점 기준:');
      item.scoring_criteria.forEach((criteria) => {
        lines.push(
          `  ${criteria.level || ''}\t${criteria.score_range || ''}\t${criteria.description || ''}`
        );
      });
    }
    if (item.details) lines.push(item.details);
    lines.push('');
  });
  if (structured.notes) lines.push(`유의사항: ${structured.notes}`);
  return lines.join('\n').trim();
};
const createHwpPayloadSignature = ({
  planText,
  planStructured,
  instructions,
}) => {
  const value = JSON.stringify({
    plan_text: planText || '',
    plan_structured: planStructured || {},
    instructions: instructions || '',
  });
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `${value.length}:${(hash >>> 0).toString(16)}`;
};

const PLAN_TYPE_CONFIGS = {
  assessment: {
    label: '평가계획서',
    title: 'AI 평가계획서',
    subtitle: '작년 계획서를 올리면 AI가 올해 계획서를 만들어요',
    sectionTitle: '작년 평가계획서',
    placeholder: '작년 평가계획서를 붙여넣으세요',
    autoMessage: '학년도 업데이트 + 채점 기준 다듬기가 자동 적용돼요',
    exampleData: `2025학년도 6학년 국어과 평가계획서

1. 평가 방침
- 듣기·말하기, 읽기, 쓰기, 문법, 문학, 매체 영역을 균형 있게 평가한다
- 과정중심평가를 강화하여 수행평가 비율을 50% 이상 확보한다

2. 평가 비율
- 지필평가 50% (중간 25점 + 기말 25점)
- 수행평가 50% (토론 15점 + 글쓰기 20점 + 발표 15점)

3. 평가 항목

가. 토론 (수행평가)
- 배점: 15점 / 시기: 5월 / 방법: 토론 관찰평가
- 성취기준: [6국01-07] 절차와 규칙을 지키고 타당한 이유와 근거를 제시하며 토론한다.
- 채점기준:
  매우잘함(15점): 토론 절차를 지키며 타당한 근거를 들어 논리적으로 주장
  잘함(12점): 절차를 대부분 지키며 근거를 들어 주장
  보통(9점): 참여하나 근거 제시 부족
  노력요함(6점): 소극적 참여, 근거 없이 의견만 제시

나. 주장하는 글쓰기 (수행평가)
- 배점: 20점 / 시기: 9월 / 방법: 글쓰기
- 성취기준: [6국03-02] 적절한 근거를 사용하고 인용의 출처를 밝히며 주장하는 글을 쓴다.

다. 매체 자료 발표 (수행평가)
- 배점: 15점 / 시기: 11월 / 방법: 발표 평가
- 성취기준: [6국01-05] 자료를 선별하여 핵심 정보를 중심으로 내용을 구성하고 매체를 활용하여 발표한다.

라. 1학기 중간고사 (지필평가)
- 배점: 25점 / 시기: 7월
- 관련 성취기준: [6국01-02], [6국02-01], [6국02-02], [6국04-04]

마. 2학기 기말고사 (지필평가)
- 배점: 25점 / 시기: 12월
- 관련 성취기준: [6국02-03], [6국03-02], [6국04-03], [6국05-03]`,
    chips: [
      { key: 'expression', label: '표현 다양화', instruction: '영역, 단원, 성취기준, 평가 내용은 그대로 유지하되, 수업-평가 연계와 채점 기준의 표현을 다양하게 바꿔주세요. 같은 의미라도 다른 문장 구조와 서술 방식을 사용해주세요.' },
      { key: 'items', label: '수행평가 항목 변경', instruction: '올해 수행평가 항목을 변경해주세요. 교과 특성과 학년 수준에 맞게 수행평가 항목을 재구성하고, 각 항목의 성취기준과 채점 기준도 함께 작성해주세요.' },
      { key: 'method', label: '평가 방법 다양화', instruction: '평가 방법을 다양하게 바꿔주세요. 프로젝트, 포트폴리오, 구술, 관찰, 토의·토론 등 교과에 맞는 다양한 방법을 활용해주세요.' },
      { key: 'ratio', label: '수행평가 비율 높이기', instruction: '과정중심평가 방향에 맞게 수행평가 비율을 높여주세요. 비율 변경에 맞춰 각 항목의 배점도 함께 조정해주세요.' },
      { key: 'points', label: '배점 재분배', instruction: '항목별 배점을 균형 있게 재분배해주세요. 전체 합계를 유지하면서 채점 기준 점수 구간도 함께 재계산해주세요.' },
      { key: 'period', label: '평가 시기 분산', instruction: '평가 시기를 학기 내에서 분산 배치해주세요. 학생 부담이 특정 시기에 집중되지 않도록 해주세요.' },
    ],
    enabled: true,
  },
  lesson_plan: {
    label: '공개수업 지도안',
    title: 'AI 수업 지도안',
    subtitle: '작년 지도안을 올리면 올해에 맞게 수정해요',
    sectionTitle: '작년 수업 지도안',
    placeholder: '작년 수업 지도안(교수학습과정안)을 붙여넣으세요',
    autoMessage: '학년도 업데이트 + 수업 흐름 다듬기가 자동 적용돼요',
    exampleData: `2025학년도 6학년 국어 교수학습과정안 (공개수업)

성취기준: [6국02-03] 글이나 자료를 읽고 내용의 타당성과 표현의 적절성을 평가한다.
학습목표: 글을 읽고 주장의 타당성을 평가할 수 있다.

[도입] 5분
- 교사: 동기유발 자료 제시, 학습 문제 안내
- 학생: 학습 목표 확인

[전개] 30분
- 교사: 주장과 근거가 담긴 글 배부, 모둠 활동 안내
- 학생: 글 읽기 → 주장과 근거 찾기 → 타당성 판단 → 모둠별 정리 및 발표
- 자료: 활동지, 모둠 학습판

[정리] 5분
- 교사: 학습 내용 정리, 차시 예고
- 학생: 배움 일지 작성, 자기 평가

평가 계획: 주장의 타당성 평가 능력 (관찰평가)`,
    chips: [
      { key: 'cooperative', label: '협력학습 추가', instruction: '협력학습 요소를 수업에 반영해주세요. 모둠 활동, 짝 활동 등 학생 간 상호작용을 강화하는 방향으로 전개 부분을 재구성해주세요.' },
      { key: 'sel', label: '사회정서교육 반영', instruction: '사회정서교육(SEL) 요소를 수업에 반영해주세요. 자기인식, 관계 기술, 책임 있는 의사결정 등의 요소를 자연스럽게 포함해주세요.' },
      { key: 'digital', label: '디지털 활용 수업', instruction: '디지털 도구를 활용한 수업 활동을 포함해주세요. 태블릿, 디지털 교과서, 교육용 앱 등을 활용한 활동을 전개에 포함해주세요.' },
      { key: 'student_led', label: '학생 주도성 강화', instruction: '학생 주도적 학습 활동을 강화해주세요. 학생이 스스로 탐구하고, 발표하고, 피드백하는 구조로 수업을 재구성해주세요.' },
      { key: 'metacognition', label: '메타인지 활동', instruction: '메타인지(학습에 대한 성찰) 활동을 정리 단계에 포함해주세요. 학습 일지, 자기 평가, 배움 나눔 등의 활동을 추가해주세요.' },
    ],
    enabled: true,
  },
  project: {
    label: '프로젝트 수업',
    title: 'AI 프로젝트 수업 계획서',
    subtitle: '성취기준을 입력하면 GRASPS 기반 프로젝트를 설계해요',
    sectionTitle: '성취기준 또는 기존 자료',
    placeholder: '성취기준을 입력해 주세요. 기존 프로젝트 계획서가 있다면 붙여넣어도 돼요.',
    autoMessage: 'GRASPS 요소 자동 설계 + 차시별 계획이 생성돼요',
    exampleData: `2025학년도 6학년 국어 프로젝트 수업 계획서

교과: 국어 / 학년: 6학년 / 총 차시: 8차시
성취기준:
[6국01-05] 자료를 선별하여 핵심 정보를 중심으로 내용을 구성하고 매체를 활용하여 발표한다.
[6국06-02] 뉴스 및 각종 정보 매체 자료의 신뢰성을 평가한다.
[6국06-03] 적합한 양식과 수용자의 반응을 고려하여 복합양식 매체 자료를 제작하고 공유한다.

GRASPS 설계
- 목표(G): 매체 자료의 신뢰성을 평가하고 매체 자료를 제작한다
- 역할(R): 미정
- 대상(A): 미정
- 상황(S): 미정
- 결과물(P): 미정
- 기준(S): 정보의 신뢰성, 내용 구성의 논리성, 매체 활용 능력`,
    chips: [
      { key: 'sessions_4', label: '4차시 구성', instruction: '프로젝트를 4차시로 구성해주세요. 탐구-제작-발표-평가 단계로 설계해주세요.' },
      { key: 'sessions_8', label: '8차시 구성', instruction: '프로젝트를 8차시로 구성해주세요. 충분한 탐구와 제작 시간을 확보하여 설계해주세요.' },
      { key: 'cross_subject', label: '교과통합', instruction: '여러 교과를 통합한 융합 프로젝트로 설계해주세요. 관련 교과의 성취기준도 함께 연계해주세요.' },
      { key: 'community', label: '지역사회 연계', instruction: '지역사회와 연계한 실천적 프로젝트로 설계해주세요. 학생들이 실제 지역 문제를 탐구하고 해결하는 활동을 포함해주세요.' },
      { key: 'real_world', label: '실생활 맥락', instruction: '실생활 맥락의 진정성 있는 과제로 설계해주세요. 학생이 실제 역할을 맡아 현실적인 결과물을 만드는 구조로 해주세요.' },
    ],
    enabled: true,
  },
  essay_eval: {
    label: '서논술형 평가',
    title: 'AI 서논술형 평가 계획서',
    subtitle: '성취기준을 입력하면 문항과 채점기준을 만들어요',
    sectionTitle: '성취기준 또는 기존 평가 자료',
    placeholder: '성취기준을 입력해 주세요. 기존 서논술형 평가 자료가 있다면 붙여넣어도 돼요.',
    autoMessage: '성취기준 분석 → 문항 개발 → 채점기준(루브릭) → 예시답안이 자동 생성돼요',
    exampleData: `2025학년도 6학년 국어 서논술형 평가

성취기준: [6국02-03] 글이나 자료를 읽고 내용의 타당성과 표현의 적절성을 평가한다.
평가요소: 주장과 근거 파악, 타당성 평가, 자기 의견 서술

평가 유형: 서술형
배점: 10점
조건: 주장과 근거를 정리하고, 타당하지 않은 근거를 이유와 함께 서술`,
    chips: [
      { key: 'discussion', label: '토의토론형', instruction: '토의·토론 활동과 연계한 서논술형 평가를 설계해주세요. 학생의 의사소통 역량을 평가할 수 있는 문항을 만들어주세요.' },
      { key: 'project_type', label: '프로젝트형', instruction: '프로젝트 활동의 결과물을 평가하는 서논술형 문항을 설계해주세요. 과정과 결과를 모두 평가할 수 있도록 해주세요.' },
      { key: 'inquiry_report', label: '탐구보고서형', instruction: '탐구 활동 결과를 보고서로 작성하는 평가를 설계해주세요. 탐구 과정, 결과 분석, 결론 도출 능력을 평가해주세요.' },
      { key: 'descriptive', label: '서술형 위주', instruction: '서술형 문항 위주로 설계해주세요. 단답형이 아닌 과정과 이유를 설명하는 문항으로 구성해주세요.' },
      { key: 'argumentative', label: '논술형 위주', instruction: '논술형 문항 위주로 설계해주세요. 자신의 주장을 근거를 들어 논리적으로 서술하는 문항으로 구성해주세요.' },
    ],
    enabled: true,
  },
  inquiry: {
    label: '탐구질문 만들기',
    title: 'AI 탐구질문 생성',
    subtitle: '성취기준을 입력하면 탐구질문을 만들어요',
    sectionTitle: '성취기준',
    placeholder: '성취기준을 입력해 주세요 (예: [6국02-03] 글이나 자료를 읽고 내용의 타당성과 표현의 적절성을 평가한다.)',
    autoMessage: '핵심 아이디어 추출 → 핵심 문장 → 탐구질문이 자동 생성돼요',
    exampleData: `6학년 국어 읽기 영역

성취기준:
[6국02-01] 글의 구조를 고려하며 주제나 주장을 파악하고 글 내용을 요약한다.
[6국02-03] 글이나 자료를 읽고 내용의 타당성과 표현의 적절성을 평가한다.
[6국02-04] 문제 상황과 관련된 다양한 관점의 글을 읽고 이를 문제 해결에 활용한다.`,
    chips: [
      { key: 'factual', label: '사실적 질문 포함', instruction: '지식을 확인하는 사실적 질문도 포함해주세요. 기본 개념 이해를 확인할 수 있는 질문을 만들어주세요.' },
      { key: 'conceptual', label: '개념적 질문 중심', instruction: '개념의 이해와 연결을 묻는 개념적 질문을 중심으로 만들어주세요. 전이 가능한 이해를 촉진하는 질문으로 구성해주세요.' },
      { key: 'debatable', label: '논쟁적 질문 포함', instruction: '다양한 관점과 판단을 요구하는 논쟁적 질문을 포함해주세요. 학생들이 토론할 수 있는 열린 질문을 만들어주세요.' },
      { key: 'transfer', label: '전이 가능 질문', instruction: '다른 맥락에도 적용 가능한 전이 질문을 만들어주세요. 교과를 넘어 삶과 연결되는 큰 질문을 포함해주세요.' },
      { key: 'unit_flow', label: '단원 흐름 설계', instruction: '단원 전체를 관통하는 핵심 질문과 차시별 하위 탐구질문을 함께 설계해주세요. 수업 흐름이 자연스럽게 연결되도록 해주세요.' },
    ],
    enabled: true,
  },
  autonomous: {
    label: '자율시간 계획서',
    title: 'AI 학교자율시간 계획서',
    subtitle: '성취기준과 주제를 입력하면 33차시 이상 계획을 설계해요',
    sectionTitle: '연계 성취기준 또는 기존 자료',
    placeholder: '연계할 성취기준과 주제를 입력해 주세요. 기존 계획서가 있다면 붙여넣어도 돼요.',
    autoMessage: '교과연계 근거 + 33차시 이상 체계적 차시별 계획이 자동 생성돼요',
    exampleData: `6학년 학교자율시간

유형: 교과연계형
총 차시: 34차시 (1학기)

연계 성취기준:
[6국06-02] 뉴스 및 각종 정보 매체 자료의 신뢰성을 평가한다.
[6국06-03] 적합한 양식과 수용자의 반응을 고려하여 복합양식 매체 자료를 제작하고 공유한다.
[6국06-04] 자신의 매체 이용 양상에 대해 성찰한다.`,
    chips: [
      { key: 'subject_linked', label: '교과연계형', instruction: '특정 교과와 연계한 심화/확장 활동으로 설계해주세요. 해당 교과의 성취기준과 명확하게 연결되는 활동을 포함해주세요.' },
      { key: 'theme_integrated', label: '주제통합형', instruction: '여러 교과를 통합한 주제 중심 탐구로 설계해주세요. 융합적 사고를 기를 수 있는 활동을 포함해주세요.' },
      { key: 'career', label: '진로탐색형', instruction: '진로 탐색과 연계한 체험 활동으로 설계해주세요. 학생의 진로 인식과 탐색을 지원하는 활동을 포함해주세요.' },
      { key: 'community_linked', label: '지역연계형', instruction: '지역사회와 연계한 실천 활동으로 설계해주세요. 지역의 특성과 자원을 활용한 프로그램을 포함해주세요.' },
      { key: 'sessions_34', label: '34차시 구성', instruction: '34차시로 구성해주세요. 한 학기(17주) 동안 주 2시간씩 운영하는 구조로 설계해주세요.' },
    ],
    enabled: true,
  },
};

const TEACHER_TIPS = [
  '커피 한 모금 하고 오세요',
  '선생님, 연초 준비 수고 많으셨어요',
  '올해도 좋은 수업 되실 거예요',
  '잠깐 스트레칭 한번 어때요?',
  '작년보다 더 좋은 계획서가 될 거예요',
  '계획서 때문에 밤새던 시절은 이제 안녕이에요',
];

// 평가계획서도 일반 첨부 정책과 동일하게 최대 10개까지 허용한다.
const MAX_FILES = MAX_FILES_DEFAULT;

const AssessmentPlanPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isAnonymous } = useAuth();
  const { subscriptionStatus } = useSubscription();
  const isPro = subscriptionStatus?.isPro || false;
  const isAnonymousUser = isAnonymous || !currentUser;
  const { showToast, removeToast } = useToast();

  // 계획서 유형 선택
  const [selectedPlanType, setSelectedPlanType] = useState('assessment');
  const planConfig = PLAN_TYPE_CONFIGS[selectedPlanType];
  const [lastAnalysisMode, setLastAnalysisMode] = useState('basic');
  const proDefaultModeAppliedRef = useRef(false);

  // 입력 상태
  const [inputMode, setInputMode] = useState('text');
  const [planText, setPlanText] = useState('');
  // B-2 후속: 성취기준 찾기(생기부 폼과 같은 피커). 학교급은 이 페이지에서만 기억한다.
  const [isStandardPickerOpen, setIsStandardPickerOpen] = useState(false);
  const [isDraftingFromStandards, setIsDraftingFromStandards] = useState(false);
  const draftUndoRef = useRef(null); // 초안으로 덮기 전 입력 원문(되돌리기용)
  const [standardSchoolLevel, setStandardSchoolLevel] = useState(() => {
    try {
      const saved = localStorage.getItem('assessment-plan-standard-school-level');
      return ['초등', '중등', '고등'].includes(saved) ? saved : '초등';
    } catch (_) {
      return '초등';
    }
  });
  const [planFiles, setPlanFiles] = useState([]);
  const [instructions, setInstructions] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [selectedHwpTargetKey, setSelectedHwpTargetKey] = useState('');
  const assessmentUploadSessionRef = useRef(
    `assessment-plan-${createAssessmentRequestId()}`
  );

  // 🆕 [버그수정] auto-resize textarea ref
  //   - planText가 빈 문자열로 리셋(예: 타입 변경)될 때 inline style.height가
  //     남아 textarea가 부풀어 보이던 문제 해결용. useLayoutEffect로 자동 동기화.
  const planTextareaRef = useRef(null);
  const assessmentAssistDetailsRef = useRef(null);

  useLayoutEffect(() => {
    const ta = planTextareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.max(ta.scrollHeight, 120) + 'px';
  }, [planText]);

  const closeAssessmentAssistGuide = useCallback(() => {
    if (assessmentAssistDetailsRef.current) {
      assessmentAssistDetailsRef.current.open = false;
    }
  }, []);

  useEffect(() => {
    if (!isPro || proDefaultModeAppliedRef.current) return;
    setLastAnalysisMode('detailed');
    proDefaultModeAppliedRef.current = true;
  }, [isPro]);

  // 결과 상태
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const loadingStageTimersRef = useRef([]);
  const [loadingHasHwp, setLoadingHasHwp] = useState(false);

  // 여러 교과 감지 상태
  const [detectedSubjects, setDetectedSubjects] = useState([]);
  // 여러 항목 감지 시 바텀시트로 선택을 묻는다(만들기 직후 화면 아래 카드만 뜨면 인지하기 어려움)
  const [isSubjectSheetOpen, setIsSubjectSheetOpen] = useState(false);
  const [sheetSelectedIds, setSheetSelectedIds] = useState([]); // 시트에서 체크한 항목 id (여러 개 가능)
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);

  // 수정 상태
  const [refineText, setRefineText] = useState('');
  const [isRefining, setIsRefining] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [hwpEditorStatus, setHwpEditorStatus] = useState('ready');
  const [isHwpSyncing, setIsHwpSyncing] = useState(false);

  // 개요 접기 상태 (기본 펼침 - 교사에게 목적/방침은 중요한 참고 자료)
  const [isOverviewExpanded, setIsOverviewExpanded] = useState(true);
  const [isOverviewEditing, setIsOverviewEditing] = useState(false);
  const resultsTopRef = useRef(null);

  // 아코디언 상태
  const [expandedItems, setExpandedItems] = useState(new Set());

  // 항목 단위 AI 수정
  const [itemRefineTexts, setItemRefineTexts] = useState({});
  const [refiningItemIndex, setRefiningItemIndex] = useState(null);
  const [refineLoadingMsgIdx, setRefineLoadingMsgIdx] = useState(0);

  // 단계별 퍼널 계측용 ref (C-1)
  const pageOpenedAtRef = useRef(Date.now());
  const inputReadyTrackedRef = useRef(false);
  const inputSourceRef = useRef(null);
  const prevPlanTypeRef = useRef(null);
  const subjectsDetectedTrackedRef = useRef(false);
  const generateRunRef = useRef(null);
  const prevRefineRef = useRef({ all: false, item: null });
  const prevIsSavedRef = useRef(null);

  // 파일 체크 로딩 상태
  const [isFileChecking, setIsFileChecking] = useState(false);
  const [checkingFileName, setCheckingFileName] = useState('');

  // 모달 상태
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const [showPreCheckModal, setShowPreCheckModal] = useState(false);
  const [preCheckResult, setPreCheckResult] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);

  // 비로그인 체험 카운터
  const [anonPlanCount, setAnonPlanCount] = useState(() => {
    return parseInt(sessionStorage.getItem('assessment-anon-count') || '0');
  });

  const [showPreview] = useState(() => {
    try {
      const count = parseInt(localStorage.getItem('assessment-preview-views') || '0', 10);
      localStorage.setItem('assessment-preview-views', String(Math.min(count + 1, 4)));
      return count < 3;
    } catch { return false; }
  });

  // 원본 데이터 (저장용)
  const previousPlanRef = useRef('');
  const initialInstructionsRef = useRef('');
  const revisionHistoryRef = useRef([]);
  const sourcePlanIdRef = useRef('');
  const sourceContextMetaRef = useRef({});
  const resultRef = useRef(null);
  const saveRequestRef = useRef({
    result: null,
    requestId: '',
  });
  const hwpEditorRef = useRef(null);
  // 작업 저장이 실패해 직접 고친 내용을 서버에 남길 수 없을 때만 "수정 내용 빼고 진행"을 연다.
  const hwpWorkSaveFailedRef = useRef(false);
  const skipHwpDirtyCheckRef = useRef(false);
  const handleSaveRef = useRef(null);
  const handleRefineRef = useRef(null);

  // 사용자 수 통계 (비로그인 미리보기용)
  const [teacherCount, setTeacherCount] = useState(1500);
  useEffect(() => {
    if (!isAnonymousUser) return;
    axios.get(getApiUrl('/api/public-stats'))
      .then(res => { if (res.data?.total_teachers) setTeacherCount(res.data.total_teachers); })
      .catch(() => {});
  }, [isAnonymousUser]);

  // 로그인 후 입력 데이터 + 결과 복원
  useEffect(() => {
    if (currentUser && !isAnonymous) {
      const savedPlan = localStorage.getItem('assessment-saved-plan');
      const savedInstructions = localStorage.getItem('assessment-saved-instructions');
      const savedResult = localStorage.getItem('assessment-saved-result');
      if (savedPlan) { setPlanText(savedPlan); localStorage.removeItem('assessment-saved-plan'); }
      if (savedInstructions) { setInstructions(savedInstructions); localStorage.removeItem('assessment-saved-instructions'); }
      if (savedResult) {
        try {
          const parsed = JSON.parse(savedResult);
          setResult(parsed);
          resultRef.current = parsed;
          if (parsed.analysis_mode === 'basic' || parsed.analysis_mode === 'detailed') {
            setLastAnalysisMode(parsed.analysis_mode);
          }
        } catch {}
        localStorage.removeItem('assessment-saved-result');
      }
    }
  }, [currentUser, isAnonymous]);

  // 비로그인 입력 자동 저장 (디바운스 1초)
  useEffect(() => {
    if (isAnonymousUser && (planText.trim() || instructions.trim())) {
      const timer = setTimeout(() => {
        localStorage.setItem('assessment-saved-plan', planText);
        localStorage.setItem('assessment-saved-instructions', instructions);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [planText, instructions, isAnonymousUser]);

  const checkLogin = useCallback(() => {
    if (!currentUser) { setShowLoginModal(true); return false; }
    return true;
  }, [currentUser]);

  const checkProAccess = useCallback(() => {
    if (!currentUser) { setShowLoginModal(true); return false; }
    if (!isPro) { setShowProModal(true); return false; }
    return true;
  }, [currentUser, isPro]);

  const hwpPlanFiles = useMemo(
    () => planFiles.filter(isHwpPlanFile),
    [planFiles]
  );
  const selectedHwpTarget = useMemo(() => {
    if (hwpPlanFiles.length === 1) return hwpPlanFiles[0];
    return hwpPlanFiles.find(
      (file) => assessmentFileKey(file) === selectedHwpTargetKey
    ) || null;
  }, [hwpPlanFiles, selectedHwpTargetKey]);

  useEffect(() => {
    if (hwpPlanFiles.length === 1) {
      setSelectedHwpTargetKey(assessmentFileKey(hwpPlanFiles[0]));
      return;
    }
    if (
      selectedHwpTargetKey
      && !hwpPlanFiles.some(
        (file) => assessmentFileKey(file) === selectedHwpTargetKey
      )
    ) {
      setSelectedHwpTargetKey('');
    }
  }, [hwpPlanFiles, selectedHwpTargetKey]);

  // ──────────────────────────────────────
  // 파일 업로드 핸들러
  // ──────────────────────────────────────
  const handleFileUpload = useCallback(async (files) => {
    if (!checkProAccess()) return;
    const incoming = Array.from(files);
    if (incoming.length === 0) return;

    const totalFiles = planFiles.length + incoming.length;
    if (totalFiles > MAX_FILES) {
      showToast(`파일은 최대 ${MAX_FILES}개까지 가능해요`, 'warning');
      return;
    }
    // 📎 파일 크기 (Pro 30MB / Free 30MB)
    const { fileSize: maxFileSize } = getFileLimits(isPro);
    const oversized = incoming.filter(f => f.size > maxFileSize);
    if (oversized.length > 0) {
      const mb = Math.round(maxFileSize / (1024 * 1024));
      showToast(`파일 크기는 ${mb}MB 이하만 가능해요`, 'warning');
      return;
    }

    const combinedFiles = [...planFiles, ...incoming];
    const totalRequestFileSize = combinedFiles.reduce((sum, f) => sum + (f.size || 0), 0);
    const MAX_REQUEST_FILE_SIZE = 25 * 1024 * 1024;
    if (totalRequestFileSize > MAX_REQUEST_FILE_SIZE) {
      setPreCheckResult({
        can_proceed: false,
        needs_confirmation: false,
        overall_status: 'error',
        title: '파일 용량을 줄여주세요',
        message: `평가계획서 파일 합계가 ${(totalRequestFileSize / 1024 / 1024).toFixed(1)}MB예요. 한 번에 25MB까지만 첨부할 수 있어요.`,
        total_files: incoming.length,
        total_size: totalRequestFileSize,
        total_size_display: `${(totalRequestFileSize / 1024 / 1024).toFixed(1)}MB`,
        files_info: incoming.map(f => ({
          filename: f.name,
          status: 'warning',
          file_size: f.size,
          file_size_display: `${(f.size / 1024 / 1024).toFixed(1)}MB`,
          issues: [],
        })),
        recommendations: ['파일을 압축하거나 나눠서 첨부해 주세요.'],
      });
      setPendingFiles(null);
      setShowPreCheckModal(true);
      return;
    }

    setIsFileChecking(true);
    setCheckingFileName(incoming.map(f => f.name).join(', '));

    try {
      const result = await preCheckFiles(incoming, 'lab', isPro);
      if (result && (result.overall_status === 'error' || result.overall_status === 'warning')) {
        setPreCheckResult(result);
        setPendingFiles(incoming);
        setShowPreCheckModal(true);
        return;
      }
    } catch {
    } finally {
      setIsFileChecking(false);
      setCheckingFileName('');
    }

    setPlanFiles(prev => [...prev, ...incoming]);
  }, [checkProAccess, planFiles, showToast, isPro]);

  // B-3: 자료함에서 작년 계획서 고르기 — 본문 텍스트를 직접 입력 칸에 넣는다(기존 텍스트 경로 재사용)
  const handlePlanLibraryPick = useCallback(async (material) => {
    if (!material?.id || !currentUser) return;
    try {
      const detail = await getReferenceMaterial(currentUser, material.id);
      // 응답은 { success, material: { content } } 형태
      const content = String(detail?.material?.content ?? detail?.content ?? '').trim();
      if (!content) {
        showToast('이 자료에는 읽을 수 있는 내용이 없어요', 'info');
        return;
      }
      inputSourceRef.current = 'library';
      setPlanFiles([]);
      setSelectedHwpTargetKey('');
      setPlanText(content);
      setInputMode('text');
      showToast(`자료함의 "${material.title || '자료'}"를 불러왔어요`, 'success');
    } catch (error) {
      console.error('자료함 계획서 불러오기 실패:', error);
      showToast('자료함에서 불러오지 못했어요', 'error');
    }
  }, [currentUser, showToast]);

  const handleStandardSchoolLevelChange = useCallback((level) => {
    setStandardSchoolLevel(level);
    try { localStorage.setItem('assessment-plan-standard-school-level', level); } catch (_) { /* noop */ }
  }, []);

  // 고른 성취기준을 `[코드] 문장` 줄로 입력 칸 끝에 붙인다(이미 있는 코드는 건너뜀 — 생기부 폼과 같은 규칙)
  const handleInsertStandards = useCallback((records) => {
    if (!Array.isArray(records) || records.length === 0) {
      setIsStandardPickerOpen(false);
      return;
    }
    inputSourceRef.current = 'standards';
    setPlanText((prev) => appendStandardLines(prev, records));
    setInputMode('text');
    setIsStandardPickerOpen(false);
    showToast(`성취기준 ${records.length}개를 넣었어요`, 'success');
  }, [showToast]);

  // 입력 칸에 `[코드]` 줄이 있을 때만 "성취기준으로 초안 만들기"를 보인다(생기부 평가요소 제안과 같은 규칙)
  const hasStandardLines = useMemo(
    () => /^\s*\[[^\]\n]+\]/m.test(planText || ''),
    [planText]
  );

  const handleDraftFromStandards = useCallback(async () => {
    if (isDraftingFromStandards || !hasStandardLines) return;
    setIsDraftingFromStandards(true);
    try {
      const response = await draftPlanFromStandards({
        text: planText,
        planType: selectedPlanType,
        schoolLevel: standardSchoolLevel,
      });
      const draft = String(response?.draft_text || '').trim();
      if (!draft) throw new Error('초안을 만들지 못했어요. 다시 시도해주세요.');
      draftUndoRef.current = planText;
      inputSourceRef.current = 'standards';
      setPlanText(draft);
      setInputMode('text');
      requestAnimationFrame(() => {
        const el = planTextareaRef.current;
        if (el) {
          el.style.height = 'auto';
          el.style.height = Math.max(el.scrollHeight, 120) + 'px';
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
      // 고시 원문을 못 찾아 교사 문장으로 만든 코드, 문장도 없어 빠진 코드는 같은 토스트 한 줄에서만 알린다(추가 UI 없음)
      const meta = response?.curriculum_grounding || {};
      const teacherCodes = (Array.isArray(response?.teacher_grounded) ? response.teacher_grounded : [])
        .map((item) => (item?.code ? `[${String(item.code).replace(/^\[|\]$/g, '')}]` : ''))
        .filter(Boolean);
      const listCodes = (codes) => `${codes.slice(0, 2).join(', ')}${codes.length > 2 ? ` 외 ${codes.length - 2}개` : ''}`;
      const teacherNote = teacherCodes.length > 0
        ? ` ${listCodes(teacherCodes)}는 고시 원문을 찾지 못해 적어주신 문장으로 만들었어요.`
        : '';
      const skipped = [...(meta.unknown_codes || []), ...(meta.ambiguous_codes || [])]
        .filter((code) => code && !teacherCodes.includes(code));
      const skippedNote = skipped.length > 0
        ? ` ${listCodes(skipped)}는 확인되지 않아 빼고 만들었어요.`
        : '';
      showToast(`초안을 넣었어요.${teacherNote}${skippedNote} 시기·배점을 고치고 만들기를 눌러 주세요`, 'success', 6000, {
        label: '되돌리기',
        onClick: () => {
          if (draftUndoRef.current !== null) {
            setPlanText(draftUndoRef.current);
            draftUndoRef.current = null;
          }
        },
      });
    } catch (error) {
      showToast(error?.message || '초안을 만들지 못했어요. 다시 시도해주세요.', 'error');
    } finally {
      setIsDraftingFromStandards(false);
    }
  }, [isDraftingFromStandards, hasStandardLines, planText, selectedPlanType, standardSchoolLevel, showToast]);

  const handleFileInputChange = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) handleFileUpload(files);
    e.target.value = '';
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); setIsDragging(false); }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleFileUpload(files);
  }, [handleFileUpload]);

  const handleFileRemove = useCallback((index) => {
    setPlanFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  // ──────────────────────────────────────
  // 생성하기
  // ──────────────────────────────────────
  const stopLoadingStageTimers = useCallback(() => {
    loadingStageTimersRef.current.forEach((timer) => clearTimeout(timer));
    loadingStageTimersRef.current = [];
  }, []);

  useEffect(() => {
    return () => stopLoadingStageTimers();
  }, [stopLoadingStageTimers]);

  useEffect(() => {
    const continueRecord = location.state?.continueRecord;
    if (continueRecord && !result) {
      if (continueRecord.plan_type && PLAN_TYPE_CONFIGS[continueRecord.plan_type]) {
        setSelectedPlanType(continueRecord.plan_type);
      }
      const restored = {
        plan_text: continueRecord.plan_text || '',
        plan_structured: continueRecord.plan_structured || {},
        analysis_mode: continueRecord.analysis_mode || 'basic',
        web_search_status: continueRecord.web_search_status || 'not_requested',
        web_search_sources: Array.isArray(continueRecord.web_search_sources)
          ? continueRecord.web_search_sources
          : [],
        hwp_result: continueRecord.hwp_result || null,
        hwp_source: continueRecord.hwp_source || null,
      };
      setResult(restored);
      resultRef.current = restored;
      setHwpEditorStatus('ready');
      setLastAnalysisMode(restored.analysis_mode);
      previousPlanRef.current = continueRecord.previous_plan_text || '';
      initialInstructionsRef.current = continueRecord.initial_instructions || '';
      revisionHistoryRef.current = Array.isArray(continueRecord.revision_history)
        ? continueRecord.revision_history
        : [];
      sourcePlanIdRef.current = continueRecord.id || continueRecord.source_plan_id || '';
      sourceContextMetaRef.current = continueRecord.source_context_meta || {};
      setIsSaved(false);
      setIsOverviewExpanded(true);
      setIsOverviewEditing(false);
      showToast('기록을 불러왔어요. 이어서 고칠 수 있어요', 'success');
      window.history.replaceState({}, document.title);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const REFINE_LOADING_MESSAGES = useMemo(() => [
    'AI가 수정사항을 분석하고 있어요...',
    '교과 맥락을 파악하고 있어요...',
    '내용을 다듬고 있어요...',
    '수정을 반영하고 있어요...',
    '마무리 정리 중이에요...',
    '거의 다 됐어요...',
  ], []);

  const [itemRefineProgress, setItemRefineProgress] = useState(0);
  const itemRefineProgressRef = useRef(null);

  useEffect(() => {
    if (refiningItemIndex === null) {
      setRefineLoadingMsgIdx(0);
      setItemRefineProgress(0);
      if (itemRefineProgressRef.current) {
        clearInterval(itemRefineProgressRef.current);
        itemRefineProgressRef.current = null;
      }
      return;
    }
    const msgInterval = setInterval(() => {
      setRefineLoadingMsgIdx((prev) => Math.min(prev + 1, REFINE_LOADING_MESSAGES.length - 1));
    }, 10000);
    let progress = 0;
    // 항목 수정 실측 p50 10초·p90 15초(refine N=118) → 초당 6%
    itemRefineProgressRef.current = setInterval(() => {
      progress = Math.min(progress + 6, 92);
      setItemRefineProgress(Math.round(progress));
    }, 1000);
    return () => {
      clearInterval(msgInterval);
      if (itemRefineProgressRef.current) {
        clearInterval(itemRefineProgressRef.current);
        itemRefineProgressRef.current = null;
      }
    };
  }, [refiningItemIndex, REFINE_LOADING_MESSAGES.length]);

  const hasInFlightWork = (
    isLoading
    || isRefining
    || refiningItemIndex !== null
    || isHwpSyncing
  );

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasInFlightWork || (result && !isSaved)) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasInFlightWork, result, isSaved]);

  const navBlocker = useNavBlocker(
    hasInFlightWork || (!!result && !isSaved)
  );

  // ── 단계별 퍼널 계측(C-1): 종류 선택 → 입력 → 감지 → 실행 → 수정 → 저장 ──
  // 상태 전이만 감시해 이벤트를 남기므로 기존 핸들러 흐름에는 손대지 않는다.
  const uxBase = useCallback((extra = {}) => ({
    isPro,
    isAnonymous: isAnonymousUser,
    planType: selectedPlanType,
    analysisMode: lastAnalysisMode,
    sinceOpenMs: Date.now() - pageOpenedAtRef.current,
    ...extra,
  }), [isPro, isAnonymousUser, selectedPlanType, lastAnalysisMode]);
  const hasPlanInput = planText.trim().length > 0 || planFiles.length > 0;

  const showHwpUnsavedEditsToast = useCallback((target) => {
    if (!hwpWorkSaveFailedRef.current) {
      showToast('한글 문서에서 직접 고친 내용은 작업 저장을 먼저 눌러주세요', 'warning');
      return;
    }
    const isRefine = target === 'refine';
    let toastId = null;
    toastId = showToast(
      isRefine
        ? '작업 저장을 못 한 직접 수정이 있어요. 그대로 AI 수정을 하면 직접 고친 내용은 사라져요'
        : '작업 저장을 못 한 직접 수정이 있어요. 그대로 저장하면 직접 고치기 전 한글 문서가 기록에 남아요',
      'warning',
      12000,
      {
        label: isRefine ? '수정 내용 빼고 AI 수정' : '수정 내용 빼고 저장',
        onClick: () => {
          if (toastId) removeToast(toastId);
          // 토스트가 떠 있는 동안 작업 저장을 다시 눌렀다면 직접 수정은 아직 살릴 수 있다.
          const skipEdits = hwpWorkSaveFailedRef.current;
          skipHwpDirtyCheckRef.current = skipEdits;
          if (skipEdits) trackAssessmentPlanUxEvent('hwp_skip_edits', uxBase({ target }));
          const proceed = isRefine ? handleRefineRef.current : handleSaveRef.current;
          void proceed?.();
        },
      },
      { replaceKey: 'assessment-hwp-unsaved-edits' },
    );
  }, [showToast, removeToast, uxBase]);

  useEffect(() => {
    hwpWorkSaveFailedRef.current = false;
  }, [result?.hwp_result?.file_id]);
  const currentInputSource = planFiles.length > 0 ? 'file' : 'text';

  // 1) 계획서 종류 변경 (첫 렌더 기본값은 제외)
  useEffect(() => {
    const prev = prevPlanTypeRef.current;
    prevPlanTypeRef.current = selectedPlanType;
    if (prev === null || prev === selectedPlanType) return;
    trackAssessmentPlanUxEvent('type_selected', uxBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlanType]);

  // 2) 재료가 처음 채워진 순간 (예시 채우기·파일·직접 입력 구분)
  useEffect(() => {
    if (!hasPlanInput) { inputReadyTrackedRef.current = false; return; }
    if (inputReadyTrackedRef.current) return;
    inputReadyTrackedRef.current = true;
    const source = inputSourceRef.current || currentInputSource;
    inputSourceRef.current = null;
    trackAssessmentPlanUxEvent('input_ready', uxBase({ source }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPlanInput]);

  // 3) 여러 항목 감지
  useEffect(() => {
    if (detectedSubjects.length === 0) { subjectsDetectedTrackedRef.current = false; return; }
    if (subjectsDetectedTrackedRef.current) return;
    subjectsDetectedTrackedRef.current = true;
    trackAssessmentPlanUxEvent('subjects_detected', uxBase({ itemCount: detectedSubjects.length }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectedSubjects.length]);

  // 4) 생성 시작/완료/실패 — 로딩 전이 + 결과 객체 교체 여부로 판정
  useEffect(() => {
    const run = generateRunRef.current;
    if (isLoading && !run) {
      generateRunRef.current = { startedAt: Date.now(), resultBefore: result, source: currentInputSource };
      trackAssessmentPlanUxEvent('generate_started', uxBase({
        source: currentInputSource,
        hasHwp: planFiles.some(isHwpPlanFile),
      }));
      return;
    }
    if (!isLoading && run) {
      generateRunRef.current = null;
      const elapsedMs = Date.now() - run.startedAt;
      if (result && result !== run.resultBefore) {
        trackAssessmentPlanUxEvent('generate_completed', uxBase({
          source: run.source,
          elapsedMs,
          hasHwp: Boolean(result.hwp_result?.file_id),
        }));
      } else if (detectedSubjects.length === 0) {
        // 감지 시트가 열린 경우(여러 항목 → 선택 대기)는 실패가 아니다.
        trackAssessmentPlanUxEvent('generate_failed', uxBase({ source: run.source, elapsedMs }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // 5) AI 수정 요청 시작 (전체 / 항목)
  useEffect(() => {
    const prev = prevRefineRef.current;
    prevRefineRef.current = { all: isRefining, item: refiningItemIndex };
    if (isRefining && !prev.all) {
      trackAssessmentPlanUxEvent('refine', uxBase({ scope: 'all' }));
    } else if (refiningItemIndex !== null && prev.item === null) {
      trackAssessmentPlanUxEvent('refine', uxBase({ scope: 'item' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefining, refiningItemIndex]);

  // 6) 저장 완료
  useEffect(() => {
    const prev = prevIsSavedRef.current;
    prevIsSavedRef.current = isSaved;
    if (prev === null || !isSaved || prev) return;
    trackAssessmentPlanUxEvent('save_completed', uxBase({ hasHwp: Boolean(result?.hwp_result?.file_id) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaved]);

  const runGenerate = useCallback(async (subjectName, analysisMode = 'basic') => {
    const hasFiles = planFiles.length > 0;
    const hwpTarget = selectedHwpTarget;
    const shouldBuildHwpResult = Boolean(
      currentUser && isPro
    );
    const shouldApplyExistingHwp = Boolean(
      shouldBuildHwpResult && hasFiles && hwpTarget
    );
    let uploadedSourceFileId = '';
    let hwpUploadPromise = null;

    stopLoadingStageTimers();
    setIsLoading(true);
    setLoadingStep(1);
    setLoadingProgress(analysisMode === 'detailed' ? 18 : 24);
    setLoadingHasHwp(shouldBuildHwpResult);
    setResult(null);
    setHwpEditorStatus('ready');

    previousPlanRef.current = hasFiles ? '' : planText;
    initialInstructionsRef.current = instructions;
    revisionHistoryRef.current = [];
    sourcePlanIdRef.current = '';
    sourceContextMetaRef.current = {};

    try {
      if (shouldApplyExistingHwp) {
        hwpUploadPromise = uploadChatFile(
          hwpTarget,
          assessmentUploadSessionRef.current,
          currentUser.uid,
          null,
          'assessment_plan_source',
        );
      }
      if (analysisMode === 'detailed') {
        loadingStageTimersRef.current = [
          setTimeout(() => {
            setLoadingStep(2);
            setLoadingProgress(44);
          }, 4000),
          setTimeout(() => {
            setLoadingStep(3);
            setLoadingProgress(72);
          }, 12000),
        ];
      } else {
        loadingStageTimersRef.current = [
          setTimeout(() => {
            setLoadingStep(2);
            setLoadingProgress(58);
          }, 800),
        ];
      }

      const response = await generateAssessmentPlan(
        hasFiles ? '' : planText,
        instructions || '',
        hasFiles ? planFiles : null,
        subjectName || null, // 문자열(1개) 또는 배열(여러 항목)
        selectedPlanType,
        analysisMode,
      );

      stopLoadingStageTimers();

      if (response.success) {
        previousPlanRef.current = typeof response.source_context === 'string'
          ? response.source_context
          : (hasFiles ? '' : planText);
        sourceContextMetaRef.current = response.source_context_meta || {
          source_kind: hasFiles ? 'files' : 'text',
          file_count: hasFiles ? planFiles.length : 0,
          char_count: previousPlanRef.current.length,
          was_truncated: false,
          file_extensions: hasFiles
            ? planFiles
                .map((file) => file.name.split('.').pop()?.toLowerCase())
                .filter(Boolean)
            : [],
        };
        const structured = response.plan_structured || {};
        const hasContent = selectedPlanType === 'assessment'
          ? structured.items?.length > 0
          : (structured.lesson_flow?.length > 0 || structured.session_plan?.length > 0 ||
             structured.inquiry_questions?.length > 0 || structured.items?.length > 0 ||
             structured.core_ideas?.length > 0 || structured.overview);
        if (!hasContent) {
          showToast(`입력 내용에서 ${planConfig.label}을 찾지 못했어요. 다시 확인해주세요.`, 'warning');
          return;
        }
        let hwpResult = null;
        let hwpSource = null;
        let hwpGenerateError = null;
        if (shouldBuildHwpResult) {
          const hwpRequestId = createAssessmentRequestId();
          const hwpPayloadSignature = createHwpPayloadSignature({
            planText: response.plan_text || '',
            planStructured: structured,
            instructions: instructions || '',
          });
          try {
            setLoadingStep(analysisMode === 'detailed' ? 4 : 3);
            setLoadingProgress(78);
            let hwpResponse;
            if (shouldApplyExistingHwp && hwpUploadPromise) {
              const uploadResult = await hwpUploadPromise;
              uploadedSourceFileId = uploadResult?.file_id || '';
              if (!uploadedSourceFileId || uploadResult?.is_hwpx_form !== true) {
                throw new Error('이 한글 파일은 원본 양식을 유지하며 편집하기 어려워요.');
              }
              hwpResponse = await applyAssessmentPlanToHwp({
                sourceFileId: uploadedSourceFileId,
                requestId: hwpRequestId,
                planText: response.plan_text || '',
                planStructured: structured,
                instructions: instructions || '',
                planType: selectedPlanType,
                webSearchSources: response.web_search_sources || [],
              });
            } else {
              hwpResponse = await generateAssessmentPlanHwp({
                requestId: hwpRequestId,
                planText: response.plan_text || '',
                planStructured: structured,
                instructions: instructions || '',
                planType: selectedPlanType,
                webSearchSources: response.web_search_sources || [],
              });
            }
            setLoadingStep(analysisMode === 'detailed' ? 5 : 4);
            setLoadingProgress(94);
            if (hwpResponse?.success && hwpResponse.hwp_result?.file_id) {
              hwpResult = hwpResponse.hwp_result;
            } else {
              throw new Error(
                hwpResponse?.detail || '한글 문서를 안전하게 준비하지 못했어요.'
              );
            }
          } catch (hwpError) {
            if (uploadedSourceFileId) {
              hwpSource = {
                file_id: uploadedSourceFileId,
                filename: hwpTarget?.name || '평가계획서 한글 파일',
                request_id: hwpRequestId,
                payload_signature: hwpPayloadSignature,
              };
            } else {
              hwpGenerateError = {
                request_id: hwpRequestId,
                payload_signature: hwpPayloadSignature,
              };
            }
            showToast(
              `${hwpError.message || '한글 문서를 준비하지 못했어요.'} 계획서 내용은 그대로 보관했어요.`,
              'warning',
            );
          }
        }
        setLoadingStep(
          shouldBuildHwpResult
            ? (analysisMode === 'detailed' ? 6 : 5)
            : (analysisMode === 'detailed' ? 4 : 3)
        );
        setLoadingProgress(100);
        const newResult = {
          plan_text: response.plan_text || '',
          plan_structured: structured,
          analysis_mode: analysisMode,
          web_search_status: response.web_search_status || (
            analysisMode === 'detailed' ? 'unavailable' : 'not_requested'
          ),
          web_search_sources: Array.isArray(response.web_search_sources)
            ? response.web_search_sources
            : [],
          curriculum_grounding: response.curriculum_grounding || null,
          hwp_result: hwpResult,
          hwp_source: hwpSource,
          hwp_generate_error: hwpGenerateError,
        };
        setResult(newResult);
        resultRef.current = newResult;
        setLastAnalysisMode(analysisMode);
        setIsOverviewExpanded(true);
        setIsOverviewEditing(false);
        setIsSaved(false);
        showToast(`${planConfig.label}을 만들었어요`, 'success');
        trackRiseFeatureAction('rubric_plan', 'generate', {
          plan_type: selectedPlanType,
          analysis_mode: analysisMode,
          anonymous: isAnonymousUser,
          subject: subjectName || null,
          input_kind: hasFiles ? 'files' : 'text',
          hwp_source_count: hwpPlanFiles.length,
          hwp_result_origin: hwpResult?.result_origin
            || (hwpSource ? 'source_apply_failed' : null)
            || (hwpGenerateError ? 'generate_failed' : 'not_requested'),
        });
        if (isAnonymousUser) {
          const newCount = anonPlanCount + 1;
          setAnonPlanCount(newCount);
          sessionStorage.setItem('assessment-anon-count', String(newCount));
          try { localStorage.setItem('assessment-saved-result', JSON.stringify(newResult)); } catch {}
        }
        setTimeout(() => {
          resultsTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        throw new Error(response.detail || '만들지 못했어요. 다시 시도해 주세요.');
      }
    } catch (err) {
      stopLoadingStageTimers();
      if (hwpUploadPromise && !uploadedSourceFileId) {
        try {
          const uploadResult = await hwpUploadPromise;
          uploadedSourceFileId = uploadResult?.file_id || '';
        } catch {
          uploadedSourceFileId = '';
        }
      }
      if (uploadedSourceFileId) {
        void deleteChatFile(uploadedSourceFileId).catch(() => {});
      }
      showToast(err.message || '생성 중 오류가 발생했어요. 다시 시도해주세요.', 'error');
    } finally {
      stopLoadingStageTimers();
      setIsLoading(false);
      setLoadingStep(0);
      setLoadingProgress(0);
      setLoadingHasHwp(false);
    }
  }, [
    planText,
    planFiles,
    instructions,
    showToast,
    stopLoadingStageTimers,
    isAnonymousUser,
    anonPlanCount,
    selectedPlanType,
    planConfig.label,
    selectedHwpTarget,
    currentUser,
    isPro,
    hwpPlanFiles.length,
  ]);

  const handleGenerate = useCallback(async (analysisMode = lastAnalysisMode) => {
    const hasText = planText.trim().length > 0;
    const hasFiles = planFiles.length > 0;
    if (!hasText && !hasFiles) {
      showToast(`${planConfig.sectionTitle}을 입력해주세요`, 'warning');
      return;
    }

    if (analysisMode === 'detailed' && !isPro) {
      setShowProModal(true);
      return;
    }

    if (isAnonymousUser) {
      localStorage.setItem('assessment-saved-plan', planText);
      localStorage.setItem('assessment-saved-instructions', instructions);
      if (anonPlanCount >= 1) {
        setShowLoginModal(true);
        return;
      }
    }

    if (hasFiles && !isPro) {
      if (isAnonymousUser) {
        setShowLoginModal(true);
      } else {
        setShowProModal(true);
      }
      return;
    }

    if (hwpPlanFiles.length > 1 && !selectedHwpTarget) {
      showToast('결과를 적용할 한글 파일을 하나 선택해주세요', 'warning');
      setInputMode('file');
      return;
    }

    if (selectedPlanType === 'assessment') {
      setIsDetecting(true);
      try {
        const detectResult = await detectSubjects(hasText ? planText : '', hasFiles ? planFiles : null);
        if (detectResult.multi && detectResult.subjects?.length > 1) {
          setDetectedSubjects(detectResult.subjects);
          setSheetSelectedIds([]);
          setIsSubjectSheetOpen(true);
          setIsDetecting(false);
          return;
        }
      } catch {}
      setIsDetecting(false);
    }

    runGenerate(null, analysisMode);
  }, [
    isAnonymousUser,
    anonPlanCount,
    isPro,
    planText,
    planFiles,
    instructions,
    showToast,
    runGenerate,
    planConfig.sectionTitle,
    selectedPlanType,
    lastAnalysisMode,
    hwpPlanFiles.length,
    selectedHwpTarget,
  ]);

  const handleSubjectSelect = useCallback((subject) => {
    trackAssessmentPlanUxEvent('subject_choice', uxBase({ choice: 'single', itemCount: detectedSubjects.length }));
    setSelectedSubject(subject);
    setDetectedSubjects([]);
    setIsSubjectSheetOpen(false);
    runGenerate(subject.name, lastAnalysisMode);
  }, [runGenerate, lastAnalysisMode, uxBase, detectedSubjects.length]);

  const handleGenerateAllSubjects = useCallback(() => {
    trackAssessmentPlanUxEvent('subject_choice', uxBase({ choice: 'all', itemCount: detectedSubjects.length }));
    setDetectedSubjects([]);
    setIsSubjectSheetOpen(false);
    runGenerate(null, lastAnalysisMode);
  }, [runGenerate, lastAnalysisMode, uxBase, detectedSubjects.length]);

  const toggleSheetSubject = useCallback((subjectId) => {
    setSheetSelectedIds((prev) => (
      prev.includes(subjectId) ? prev.filter((id) => id !== subjectId) : [...prev, subjectId]
    ));
  }, []);

  // 시트 primary: 체크 0개면 전체, 1개면 기존 단일 경로, 여러 개면 이름 배열로 생성
  const handleSheetConfirm = useCallback(() => {
    const picked = detectedSubjects.filter((subject) => sheetSelectedIds.includes(subject.id));
    if (picked.length === 0) {
      handleGenerateAllSubjects();
      return;
    }
    if (picked.length === 1) {
      handleSubjectSelect(picked[0]);
      return;
    }
    trackAssessmentPlanUxEvent('subject_choice', uxBase({ choice: 'multi', itemCount: picked.length }));
    setSelectedSubject(null);
    setDetectedSubjects([]);
    setIsSubjectSheetOpen(false);
    runGenerate(picked.map((subject) => subject.name), lastAnalysisMode);
  }, [detectedSubjects, sheetSelectedIds, handleGenerateAllSubjects, handleSubjectSelect, runGenerate, lastAnalysisMode, uxBase]);

  // ──────────────────────────────────────
  // AI 수정 요청 (전체)
  // ──────────────────────────────────────
  const handleRefine = useCallback(async () => {
    const skipDirtyCheck = skipHwpDirtyCheckRef.current;
    skipHwpDirtyCheckRef.current = false;
    const revisionInstruction = refineText.trim();
    if (!revisionInstruction || !result) return;
    const currentPlan = resultRef.current || result;
    const currentHwp = currentPlan.hwp_result;
    if (currentHwp?.file_id && !skipDirtyCheck) {
      if (!hwpEditorRef.current?.flushDirtyState) {
        showToast('한글 문서를 준비하고 있어요. 잠시 후 다시 수정해주세요', 'warning');
        return;
      }
      const dirtyState = await hwpEditorRef.current.flushDirtyState();
      if (dirtyState?.checkFailed || dirtyState?.dirty) {
        if (hwpWorkSaveFailedRef.current) setHwpEditorStatus('save-error');
        else setHwpEditorStatus(dirtyState?.dirty ? 'potential-edit' : 'ready');
        showHwpUnsavedEditsToast('refine');
        return;
      }
    }

    setIsSaved(false);
    setIsRefining(true);
    try {
      const response = await refineAssessmentPlan(currentPlan, revisionInstruction, {
        originalContext: previousPlanRef.current || undefined,
        revisionHistory: revisionHistoryRef.current.length > 0 ? revisionHistoryRef.current : undefined,
        planType: selectedPlanType,
        sourcePlanId: sourcePlanIdRef.current || undefined,
      });

      if (response.success) {
        let nextHwpResult = currentHwp || null;
        if (currentHwp?.file_id) {
          setIsHwpSyncing(true);
          setHwpEditorStatus('saving');
          const hwpResponse = await applyAssessmentPlanToHwp({
            sourceFileId: currentHwp.file_id,
            requestId: createAssessmentRequestId(),
            planText: response.plan_text || '',
            planStructured: response.plan_structured || {},
            instructions: revisionInstruction,
            planType: selectedPlanType,
            webSearchSources: currentPlan.web_search_sources || [],
          });
          if (!hwpResponse?.success || !hwpResponse.hwp_result?.file_id) {
            throw new Error(
              hwpResponse?.detail || 'AI 수정 내용을 한글 문서에 반영하지 못했어요.'
            );
          }
          nextHwpResult = hwpResponse.hwp_result;
          setHwpEditorStatus('ready');
        }
        const newResult = {
          plan_text: response.plan_text || '',
          plan_structured: response.plan_structured || {},
          analysis_mode: currentPlan.analysis_mode || lastAnalysisMode,
          web_search_status: currentPlan.web_search_status || 'not_requested',
          web_search_sources: currentPlan.web_search_sources || [],
          hwp_result: nextHwpResult,
          hwp_source: currentPlan.hwp_source || null,
          curriculum_grounding: response.curriculum_grounding || currentPlan.curriculum_grounding || null,
        };
        setResult(newResult);
        resultRef.current = newResult;
        revisionHistoryRef.current = Array.isArray(response.revision_history)
          ? response.revision_history
          : [
              ...revisionHistoryRef.current,
              {
                instruction: revisionInstruction,
                timestamp: new Date().toISOString(),
              },
            ].slice(-30);
        setRefineText('');
        setIsSaved(false);
        showToast(
          currentHwp?.file_id
            ? 'AI 수정 내용을 한글 문서에 반영했어요'
            : '수정을 반영했어요',
          'success'
        );
      } else {
        throw new Error('고치지 못했어요. 다시 시도해 주세요.');
      }
    } catch (err) {
      if (currentHwp?.file_id) {
        setHwpEditorStatus('sync-error');
      }
      showToast(err.message || '수정 중 오류가 발생했어요', 'error');
    } finally {
      setIsRefining(false);
      setIsHwpSyncing(false);
    }
  }, [refineText, result, showToast, showHwpUnsavedEditsToast, selectedPlanType, lastAnalysisMode]);
  handleRefineRef.current = handleRefine;

  // ──────────────────────────────────────
  // AI 수정 요청 (항목 단위)
  // ──────────────────────────────────────
  const handleItemRefine = useCallback(async (itemIdx) => {
    const text = itemRefineTexts[itemIdx]?.trim();
    if (!text || !result) return;

    setIsSaved(false);
    setRefiningItemIndex(itemIdx);
    try {
      const currentPlan = resultRef.current || result;
      const response = await refineAssessmentPlan(currentPlan, text, {
        targetItemIndex: itemIdx,
        originalContext: previousPlanRef.current || undefined,
        revisionHistory: revisionHistoryRef.current.length > 0 ? revisionHistoryRef.current : undefined,
        planType: selectedPlanType,
        sourcePlanId: sourcePlanIdRef.current || undefined,
      });

      if (response.success) {
        const newResult = {
          plan_text: response.plan_text || '',
          plan_structured: response.plan_structured || {},
          analysis_mode: currentPlan.analysis_mode || lastAnalysisMode,
          web_search_status: currentPlan.web_search_status || 'not_requested',
          web_search_sources: currentPlan.web_search_sources || [],
          hwp_result: currentPlan.hwp_result || null,
          hwp_source: currentPlan.hwp_source || null,
          // B-2 후속: 수정 응답의 대조 결과가 있으면 갱신, 없으면(코드 없는 계획서) 이전 값 유지
          curriculum_grounding: response.curriculum_grounding || currentPlan.curriculum_grounding || null,
        };
        setResult(newResult);
        resultRef.current = newResult;
        revisionHistoryRef.current = Array.isArray(response.revision_history)
          ? response.revision_history
          : [
              ...revisionHistoryRef.current,
              {
                instruction: `[항목 ${itemIdx + 1}] ${text}`,
                timestamp: new Date().toISOString(),
                target_item_index: itemIdx,
              },
            ].slice(-30);
        setItemRefineTexts((prev) => ({ ...prev, [itemIdx]: '' }));
        setIsSaved(false);
        showToast('항목 수정을 반영했어요', 'success');
      } else {
        throw new Error('고치지 못했어요. 다시 시도해 주세요.');
      }
    } catch (err) {
      showToast(err.message || '수정 중 오류가 발생했어요', 'error');
    } finally {
      setRefiningItemIndex(null);
    }
  }, [itemRefineTexts, result, showToast, selectedPlanType, lastAnalysisMode]);

  const handleRetryHwpApply = useCallback(async () => {
    const currentResult = resultRef.current;
    const source = currentResult?.hwp_source;
    if (!source?.file_id || isHwpSyncing) return;
    const currentPlanText = selectedPlanType === 'assessment'
      ? buildAssessmentPlanTextFromStructured(
          currentResult.plan_structured
        )
      : currentResult.plan_text || '';
    const retryInstructions = instructions || '';
    const currentSignature = createHwpPayloadSignature({
      planText: currentPlanText,
      planStructured: currentResult.plan_structured || {},
      instructions: retryInstructions,
    });
    const requestId = (
      source.payload_signature === currentSignature
      && source.request_id
    )
      ? source.request_id
      : createAssessmentRequestId();
    if (
      source.request_id !== requestId
      || source.payload_signature !== currentSignature
    ) {
      const prepared = {
        ...currentResult,
        hwp_source: {
          ...source,
          request_id: requestId,
          payload_signature: currentSignature,
        },
      };
      setResult(prepared);
      resultRef.current = prepared;
    }
    setIsHwpSyncing(true);
    try {
      const response = await applyAssessmentPlanToHwp({
        sourceFileId: source.file_id,
        requestId,
        planText: currentPlanText,
        planStructured: currentResult.plan_structured || {},
        instructions: retryInstructions,
        planType: selectedPlanType,
        webSearchSources: currentResult.web_search_sources || [],
      });
      if (!response?.success || !response.hwp_result?.file_id) {
        throw new Error(
          response?.detail || '한글 양식에 다시 반영하지 못했어요.'
        );
      }
      const updated = {
        ...currentResult,
        hwp_result: response.hwp_result,
        hwp_source: null,
      };
      setResult(updated);
      resultRef.current = updated;
      setHwpEditorStatus('ready');
      setIsSaved(false);
      showToast('한글 양식에 결과를 반영했어요', 'success');
    } catch (error) {
      showToast(
        `${error.message || '한글 양식에 다시 반영하지 못했어요.'} 내용 수정 결과는 그대로 보관했어요.`,
        'warning',
      );
    } finally {
      setIsHwpSyncing(false);
    }
  }, [instructions, isHwpSyncing, selectedPlanType, showToast]);

  const handleRetryHwpGenerate = useCallback(async () => {
    const currentResult = resultRef.current;
    const failed = currentResult?.hwp_generate_error;
    if (!failed || isHwpSyncing) return;
    const currentPlanText = selectedPlanType === 'assessment'
      ? buildAssessmentPlanTextFromStructured(
          currentResult.plan_structured
        )
      : currentResult.plan_text || '';
    const retryInstructions = instructions || '';
    const currentSignature = createHwpPayloadSignature({
      planText: currentPlanText,
      planStructured: currentResult.plan_structured || {},
      instructions: retryInstructions,
    });
    const requestId = (
      failed.payload_signature === currentSignature
      && failed.request_id
    )
      ? failed.request_id
      : createAssessmentRequestId();
    setIsHwpSyncing(true);
    try {
      const response = await generateAssessmentPlanHwp({
        requestId,
        planText: currentPlanText,
        planStructured: currentResult.plan_structured || {},
        instructions: retryInstructions,
        planType: selectedPlanType,
        webSearchSources: currentResult.web_search_sources || [],
      });
      if (!response?.success || !response.hwp_result?.file_id) {
        throw new Error(
          response?.detail || '한글 문서를 다시 만들지 못했어요.'
        );
      }
      const updated = {
        ...currentResult,
        hwp_result: response.hwp_result,
        hwp_generate_error: null,
      };
      setResult(updated);
      resultRef.current = updated;
      setHwpEditorStatus('ready');
      setIsSaved(false);
      showToast('한글 문서를 만들었어요', 'success');
    } catch (error) {
      const updated = {
        ...currentResult,
        hwp_generate_error: {
          request_id: requestId,
          payload_signature: currentSignature,
        },
      };
      setResult(updated);
      resultRef.current = updated;
      showToast(
        error.message || '한글 문서를 다시 만들지 못했어요.',
        'warning',
      );
    } finally {
      setIsHwpSyncing(false);
    }
  }, [instructions, isHwpSyncing, selectedPlanType, showToast]);

  // ──────────────────────────────────────
  // 셀 직접 편집
  // ──────────────────────────────────────
  const handleCellEdit = useCallback((itemIndex, field, value) => {
    if (!result?.plan_structured?.items) return;
    const updated = { ...result };
    const items = [...updated.plan_structured.items];
    items[itemIndex] = { ...items[itemIndex], [field]: value };
    updated.plan_structured = { ...updated.plan_structured, items };
    setResult(updated);
    resultRef.current = updated;
    setIsSaved(false);
  }, [result]);

  const handleScoringCriteriaEdit = useCallback((itemIndex, criteriaIndex, field, value) => {
    if (!result?.plan_structured?.items) return;
    const updated = { ...result };
    const items = [...updated.plan_structured.items];
    const criteria = [...(items[itemIndex].scoring_criteria || [])];
    criteria[criteriaIndex] = { ...criteria[criteriaIndex], [field]: value };
    items[itemIndex] = { ...items[itemIndex], scoring_criteria: criteria };
    updated.plan_structured = { ...updated.plan_structured, items };
    setResult(updated);
    resultRef.current = updated;
    setIsSaved(false);
  }, [result]);

  // ──────────────────────────────────────
  // 저장하기
  // ──────────────────────────────────────
  const handleSave = useCallback(async () => {
    const skipDirtyCheck = skipHwpDirtyCheckRef.current;
    skipHwpDirtyCheckRef.current = false;
    const currentResult = resultRef.current || result;
    if (!currentResult) return false;
    if (!checkLogin()) return false;
    if (isSaving) return false; // 저장 요청 중 재진입 방지(확인 모달의 저장 버튼 등 다른 진입점 포함)
    let editorStateChecked = false;
    if (currentResult.hwp_result?.file_id && skipDirtyCheck) {
      editorStateChecked = true;
    } else if (currentResult.hwp_result?.file_id) {
      if (!hwpEditorRef.current?.flushDirtyState) {
        showToast('한글 문서를 준비하고 있어요. 잠시 후 기록에 저장해주세요', 'warning');
        return false;
      }
      setHwpEditorStatus('checking');
      const dirtyState = await hwpEditorRef.current.flushDirtyState();
      if (dirtyState?.checkFailed && !hwpWorkSaveFailedRef.current) {
        setHwpEditorStatus('ready');
        showToast('한글 문서의 저장 상태를 확인하지 못했어요. 작업 저장을 먼저 눌러주세요', 'warning');
        return false;
      }
      if (dirtyState?.dirty || dirtyState?.checkFailed) {
        setHwpEditorStatus(hwpWorkSaveFailedRef.current ? 'save-error' : 'potential-edit');
        showHwpUnsavedEditsToast('save');
        return false;
      }
      editorStateChecked = true;
      setHwpEditorStatus((status) => (
        status === 'checking' ? 'ready' : status
      ));
    }
    if (
      currentResult.hwp_result?.file_id
      && !editorStateChecked
      && hwpEditorStatus === 'potential-edit'
    ) {
      showToast('한글 문서에서 직접 고친 내용은 작업 저장을 먼저 눌러주세요', 'warning');
      return false;
    }
    if (isHwpSyncing) return false;
    if (saveRequestRef.current.result !== currentResult) {
      saveRequestRef.current = {
        result: currentResult,
        requestId: createAssessmentRequestId(),
      };
    }

    setIsSaving(true);
    try {
      const response = await saveAssessmentPlan({
        request_id: saveRequestRef.current.requestId,
        plan_type: selectedPlanType,
        previous_plan_text: previousPlanRef.current,
        source_context_meta: sourceContextMetaRef.current,
        source_plan_id: sourcePlanIdRef.current || undefined,
        initial_instructions: initialInstructionsRef.current,
        revision_history: revisionHistoryRef.current,
        plan_text: selectedPlanType === 'assessment'
          ? buildAssessmentPlanTextFromStructured(
              currentResult.plan_structured
            )
          : currentResult.plan_text,
        plan_structured: currentResult.plan_structured,
        analysis_mode: currentResult.analysis_mode || lastAnalysisMode,
        web_search_status: currentResult.web_search_status || 'not_requested',
        web_search_sources: currentResult.web_search_sources || [],
        hwp_result: currentResult.hwp_result || undefined,
        hwp_source: currentResult.hwp_source || undefined,
      });

      if (response.success) {
        sourcePlanIdRef.current = response.record_id || sourcePlanIdRef.current;
        setIsSaved(true);
        // B-3: 저장한 계획서를 자료함에도 한 벌 보관 (PRO만, 중복이면 건너뜀, 실패해도 저장 흐름에 영향 없음)
        if (isPro && currentUser) {
          const mirrorText = selectedPlanType === 'assessment'
            ? buildAssessmentPlanTextFromStructured(currentResult.plan_structured)
            : currentResult.plan_text;
          const planTitle = currentResult.plan_structured?.title || planConfig?.label || '평가계획서';
          mirrorTextToLibrary(currentUser, {
            title: buildLibraryMirrorTitle(planConfig?.label || '평가계획서', planTitle),
            content: mirrorText,
          }).then((outcome) => {
            if (outcome.status === 'created') {
              showToast('계획서를 자료함에도 보관했어요', 'info', 4000);
            }
          });
        }
        showToast('저장했어요', 'success', 4000, {
          label: '기록에서 확인하기',
          onClick: () => navigate('/assessment-plan-history'),
        });
        return true;
      }
      return false;
    } catch (err) {
      showToast('저장에 실패했어요. 다시 시도해주세요.', 'error');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    isSaving,
    result,
    checkLogin,
    showToast,
    showHwpUnsavedEditsToast,
    navigate,
    selectedPlanType,
    lastAnalysisMode,
    hwpEditorStatus,
    isHwpSyncing,
    isPro,
    currentUser,
    planConfig,
  ]);
  handleSaveRef.current = handleSave;

  // ──────────────────────────────────────
  // 복사하기
  // ──────────────────────────────────────
  const buildPlanTextFromStructured = useCallback((structured) => {
    return buildAssessmentPlanTextFromStructured(structured);
  }, []);

  const handleCopy = useCallback(() => {
    if (!result) return;
    trackAssessmentPlanUxEvent('copy', uxBase());
    const textToCopy = selectedPlanType === 'assessment'
      ? (buildPlanTextFromStructured(result.plan_structured) || result.plan_text || '')
      : (result.plan_text || '');
    if (!textToCopy) return;
    navigator.clipboard.writeText(textToCopy).then(() => {
      showToast('클립보드에 복사했어요', 'success');
    }).catch(() => {
      showToast('복사에 실패했어요', 'error');
    });
  }, [result, buildPlanTextFromStructured, showToast, selectedPlanType, uxBase]);

  // ──────────────────────────────────────
  // 로딩 단계 텍스트
  // ──────────────────────────────────────
  const loadingSteps = lastAnalysisMode === 'detailed'
    ? [
        { label: '최신 지침·성취기준 검색 중', done: loadingStep > 1 },
        { label: '공식 자료 근거 확인 중', done: loadingStep > 2 },
        { label: '근거를 반영해 계획서 작성 중', done: loadingStep > 3 },
        ...(loadingHasHwp
          ? [
              {
                label: selectedHwpTarget
                  ? '한글 양식에 내용 반영 중'
                  : '새 한글 문서 구성 중',
                done: loadingStep > 4,
              },
              { label: '한글 문서 열기 확인 중', done: loadingStep > 5 },
            ]
          : []),
        {
          label: '완료',
          done: loadingStep > (loadingHasHwp ? 6 : 4),
        },
      ]
    : [
        { label: '계획서 분석 중', done: loadingStep > 1 },
        { label: '구조 파악 및 생성 중', done: loadingStep > 2 },
        ...(loadingHasHwp
          ? [
              {
                label: selectedHwpTarget
                  ? '한글 양식에 내용 반영 중'
                  : '새 한글 문서 구성 중',
                done: loadingStep > 3,
              },
              { label: '한글 문서 열기 확인 중', done: loadingStep > 4 },
            ]
          : []),
        {
          label: '완료',
          done: loadingStep > (loadingHasHwp ? 5 : 3),
        },
      ];

  const [randomTip] = useState(() => TEACHER_TIPS[Math.floor(Math.random() * TEACHER_TIPS.length)]);

  // ──────────────────────────────────────
  // 아코디언 제어
  // ──────────────────────────────────────
  const toggleItemExpand = useCallback((idx) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const toggleAllItems = useCallback(() => {
    const items = result?.plan_structured?.items || [];
    setExpandedItems((prev) => {
      if (prev.size === items.length) return new Set();
      return new Set(items.map((_, i) => i));
    });
  }, [result]);

  // 결과가 새로 나타날 때(없음 → 있음) 첫 항목은 펼쳐둔다 — 전부 접힌 카드 목록만 보이면
  // 안에 뭐가 있는지 알 수 없다. refine으로 result가 갱신될 때는 사용자가 펼친 상태를 유지한다.
  const hadResultRef = useRef(false);
  useEffect(() => {
    const hasResult = Boolean(result);
    if (hasResult && !hadResultRef.current) {
      const itemCount = result?.plan_structured?.items?.length || 0;
      setExpandedItems(itemCount > 0 ? new Set([0]) : new Set());
    }
    hadResultRef.current = hasResult;
  }, [result]);

  const pointsSummary = useMemo(() => {
    const items = result?.plan_structured?.items || [];
    const byCategory = {};
    let total = 0;
    items.forEach((item) => {
      const pts = Number(item.points) || 0;
      const category = item.category || '기타';
      byCategory[category] = (byCategory[category] || 0) + pts;
      total += pts;
    });
    return { byCategory, total };
  }, [result]);

  // ──────────────────────────────────────
  // 입력 가능 여부
  // ──────────────────────────────────────
  const canGenerate = (planText.trim().length > 0 || planFiles.length > 0) && !isLoading;
  // B-2: 성취기준 코드를 교육부 고시 원문과 대조한 결과를 한 줄로 알린다(공개 데이터·개수만).
  const curriculumGroundingNotice = useMemo(
    () => buildCurriculumGroundingNotice(result?.curriculum_grounding),
    [result?.curriculum_grounding],
  );
  const globalRefinePanel = (
    <div className="assessment-refine-section assessment-refine-section-global">
      <div className="assessment-refine-header">
        <span className="assessment-refine-header-title">
          {planConfig.label} AI 수정
        </span>
      </div>
      <p className="assessment-refine-hint">
        요청을 반영한 새 한글 수정본을 만들어요.
      </p>
      <div className="assessment-refine-input-row">
        <textarea
          value={refineText}
          onChange={(e) => setRefineText(e.target.value)}
          placeholder="예: 평가 시기를 6월로 바꾸고 채점 기준을 더 구체적으로"
          className="assessment-refine-textarea"
          rows={2}
          disabled={isRefining}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleRefine();
            }
          }}
        />
        <button
          onClick={handleRefine}
          disabled={!refineText.trim() || isRefining}
          className="assessment-refine-button"
        >
          {isRefining ? '수정 중...' : 'AI 수정'}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{planConfig.title} | 왓퀴즈</title>
        <meta name="description" content={planConfig.subtitle} />
        <link rel="canonical" href="https://whatquiz.co.kr/assessment-plan" />
      </Helmet>

      <div className="assessment-page">
        <div className="assessment-header">
          <button
            className="assessment-back-button"
            onClick={() => {
              if (result) {
                if (!isSaved) {
                  setShowResetConfirm(true);
                } else {
                  setResult(null);
                  resultRef.current = null;
                  setDetectedSubjects([]);
                  setSelectedSubject(null);
                  setIsSaved(false);
                  setHwpEditorStatus('ready');
                }
              } else {
                navigate('/home');
              }
            }}
          >
            {result ? '← 새로 만들기' : '← 홈으로'}
          </button>
          <div>
            <h1 className="assessment-title">{planConfig.title}</h1>
            <p className="assessment-subtitle">
              {planConfig.subtitle}
            </p>
          </div>
        </div>

        {/* 계획서 종류 선택 (인라인 버튼) */}
        {!result && !isLoading && (
          <div className="assessment-plan-type-section">
            <p className="assessment-plan-type-label">계획서 종류</p>
            <div className="assessment-plan-type-buttons">
              {Object.entries(PLAN_TYPE_CONFIGS).map(([typeId, config]) => (
                <button
                  key={typeId}
                  className={`assessment-plan-type-btn ${selectedPlanType === typeId ? 'active' : ''} ${!config.enabled ? 'disabled' : ''}`}
                  onClick={() => {
                    if (!config.enabled) {
                      showToast('준비 중이에요. 곧 만나요!', 'info');
                      return;
                    }
                    setSelectedPlanType(typeId);
                    setPlanText('');
                    setPlanFiles([]);
                    setSelectedHwpTargetKey('');
                    setInstructions('');
                    setDetectedSubjects([]);
                    setSelectedSubject(null);
                  }}
                >
                  {config.label}
                  {!config.enabled && <span className="assessment-plan-type-soon">곧 출시</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="assessment-content">
          {/* Value First: 결과 미리보기 (비로그인 항상 + 로그인 3회까지) */}
          {(isAnonymousUser || showPreview) && !result && !isLoading && selectedPlanType === 'assessment' && (
            <div className="assessment-preview-card">
              <p className="assessment-preview-title">AI가 만든 평가계획서 미리보기</p>
              <div className="assessment-preview-result">
                <div className="assessment-preview-scroll-window" onClick={(e) => { const track = e.currentTarget.querySelector('.assessment-preview-scroll-track'); if (track) { const st = track.style; st.animationPlayState = st.animationPlayState === 'paused' ? 'running' : 'paused'; } }}>
                  <div className="assessment-preview-scroll-track">
                    <div className="assessment-points-summary" style={{ marginBottom: 8 }}>
                      <div className="assessment-points-summary-items">
                        <span className="assessment-points-summary-chip">수행평가 <strong>40점</strong></span>
                        <span className="assessment-points-summary-chip">지필평가 <strong>60점</strong></span>
                        <span className="assessment-points-summary-total">합계 <strong>100점</strong></span>
                      </div>
                    </div>

                    {/* 첫 항목: 펼침 — 성취기준 + 채점기준 인라인 */}
                    <div className="assessment-item-card expanded" style={{ marginBottom: 6 }}>
                      <div className="assessment-item-summary" style={{ cursor: 'default' }}>
                        <div className="assessment-item-summary-left">
                          <span className="assessment-item-type-badge">수행평가</span>
                          <span className="assessment-item-summary-name">곱셈구구</span>
                        </div>
                        <div className="assessment-item-summary-right">
                          <span className="assessment-item-summary-points">20점</span>
                          <span className="assessment-item-summary-period">9월</span>
                          <span className="assessment-item-summary-method">구술평가</span>
                        </div>
                      </div>
                      <div className="assessment-preview-item-detail">
                        <p className="assessment-preview-field-text">[2수01-11] 곱셈구구를 이해하고, 한 자리 수의 곱셈을 할 수 있다.</p>
                        <div className="assessment-preview-criteria-row">
                          <div className="assessment-preview-criteria-chip">
                            <span className="assessment-preview-criteria-label">매우잘함</span>
                            <span>곱셈구구를 정확히 활용하여 실생활 문제를 해결하고, 곱셈의 원리를 설명할 수 있음</span>
                          </div>
                          <div className="assessment-preview-criteria-chip">
                            <span className="assessment-preview-criteria-label">잘함</span>
                            <span>곱셈구구를 활용하여 간단한 문제를 해결할 수 있음</span>
                          </div>
                          <div className="assessment-preview-criteria-chip">
                            <span className="assessment-preview-criteria-label">보통</span>
                            <span>곱셈구구를 부분적으로 이해하며, 도움을 받아 문제를 해결함</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 나머지 항목: 접힌 상태 */}
                    {[
                      { type: '지필평가', name: '1학기 중간고사', points: '30점', period: '10월', method: '서술형', written: true,
                        standards: '[2수01-06] 두 자리 수의 범위에서 덧셈과 뺄셈의 계산 원리를 이해하고 그 계산을 할 수 있다.' },
                      { type: '수행평가', name: '규칙 찾기 탐구', points: '10점', period: '10월', method: '포트폴리오',
                        standards: '[2수02-01] 물체, 무늬, 수 등의 배열에서 규칙을 찾아 여러 가지 방법으로 표현할 수 있다.' },
                      { type: '수행평가', name: '도형 탐구 프로젝트', points: '10점', period: '11월', method: '프로젝트',
                        standards: '[2수03-04] 삼각형, 사각형, 원을 직관적으로 이해하고, 그 모양을 그릴 수 있다.' },
                      { type: '지필평가', name: '2학기 기말고사', points: '30점', period: '12월', method: '선택형+서술형', written: true,
                        standards: '[2수03-10] 길이 단위 1cm와 1m를 알고, 이를 이용하여 주변 사물의 길이를 측정할 수 있다.' },
                    ].map((item, i) => (
                      <div key={i} className="assessment-item-card" style={{ marginBottom: 6 }}>
                        <div className="assessment-item-summary" style={{ cursor: 'default' }}>
                          <div className="assessment-item-summary-left">
                            <span className={`assessment-item-type-badge ${item.written ? 'written' : ''}`}>{item.type}</span>
                            <span className="assessment-item-summary-name">{item.name}</span>
                          </div>
                          <div className="assessment-item-summary-right">
                            <span className="assessment-item-summary-points">{item.points}</span>
                            <span className="assessment-item-summary-period">{item.period}</span>
                            <span className="assessment-item-summary-method">{item.method}</span>
                          </div>
                        </div>
                        <p className="assessment-preview-standards">{item.standards}</p>
                      </div>
                    ))}
                  </div>
                  <div className="assessment-preview-fade-top" />
                  <div className="assessment-preview-fade-bottom" />
                </div>
              </div>
              <p className="assessment-preview-stats">
                <CountUp start={0} end={teacherCount} duration={2} separator="," />명의 선생님이 사용 중
              </p>
            </div>
          )}

          {/* 결과가 없을 때: 입력 영역 */}
          {!result && !isLoading && (
            <>
              {/* Section 1: 작년 계획서 */}
              <div className="assessment-section">
                <h2 className="assessment-section-title">{planConfig.sectionTitle}</h2>

                <div className="assessment-input-tabs">
                  <button
                    className={`assessment-input-tab ${inputMode === 'text' ? 'active' : ''}`}
                    onClick={() => setInputMode('text')}
                  >
                    직접 입력
                  </button>
                  <button
                    className={`assessment-input-tab ${inputMode === 'file' ? 'active' : ''}`}
                    onClick={() => setInputMode('file')}
                  >
                    파일 업로드
                    {!isPro && <span className="assessment-pro-badge">PRO</span>}
                  </button>
                </div>

                <div className="assessment-input-container">
                  {inputMode === 'text' && (
                    <textarea
                      ref={planTextareaRef}
                      value={planText}
                      onChange={(e) => {
                        setPlanText(e.target.value);
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.max(e.target.scrollHeight, 120) + 'px';
                      }}
                      placeholder={planConfig.placeholder}
                      className="assessment-textarea"
                      rows={4}
                    />
                  )}
                  {inputMode === 'text' && (
                    <div className="assessment-standards-row">
                      <AchievementStandardEntry
                        hint={hasStandardLines ? undefined : '찾아 넣으면 그 성취기준에 맞춰 만들어요'}
                        onOpen={() => setIsStandardPickerOpen(true)}
                        testId="asp-open-assessment"
                      />
                      {hasStandardLines && (
                        <button
                          type="button"
                          className={`asp-entry__btn assessment-standards-draft-btn ${isDraftingFromStandards ? 'is-busy' : ''}`}
                          data-testid="assessment-draft-from-standards"
                          onClick={handleDraftFromStandards}
                          disabled={isDraftingFromStandards}
                          aria-busy={isDraftingFromStandards}
                        >
                          {isDraftingFromStandards ? (
                            <>
                              <span className="assessment-standards-draft-spinner" aria-hidden="true" />
                              초안 만드는 중…
                            </>
                          ) : (
                            <>
                              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" />
                                <path d="M19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8L19 16z" />
                              </svg>
                              <span>성취기준으로 <b>{planConfig.label} 초안</b> 만들기</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {inputMode === 'file' && (
                    !isPro ? (
                      <div className="assessment-dropzone assessment-dropzone-pro-lock">
                        <div className="assessment-pro-lock-content">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8b95a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                          </svg>
                          <span className="assessment-pro-lock-text">PRO에서 파일을 업로드할 수 있어요</span>
                          <span className="assessment-pro-lock-hint">PDF, HWP, PPT, 이미지, Excel 지원</span>
                          <button className="assessment-pro-lock-cta" onClick={() => setShowProModal(true)}>
                            PRO 시작하기
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`assessment-dropzone ${isDragging ? 'dragging' : ''} ${isFileChecking ? 'checking' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        {isFileChecking ? (
                          <div className="assessment-file-checking">
                            <div className="assessment-file-checking-spinner" />
                            <span className="assessment-file-checking-name">{checkingFileName}</span>
                            <span className="assessment-file-checking-status">파일 확인 중...</span>
                          </div>
                        ) : planFiles.length > 0 ? (
                          <div className="assessment-files-attached">
                            <div className="assessment-file-list">
                              {planFiles.map((file, index) => (
                                <div key={index} className="assessment-file-item">
                                  <span className="assessment-file-item-name">{file.name}</span>
                                  <button onClick={() => handleFileRemove(index)} className="assessment-file-item-remove">✕</button>
                                </div>
                              ))}
                            </div>
                            {hwpPlanFiles.length > 1 && (
                              <fieldset className="assessment-hwp-target-picker">
                                <legend>결과를 적용할 한글 파일</legend>
                                <p>나머지 파일도 참고 자료로 함께 분석해요.</p>
                                <div className="assessment-hwp-target-options">
                                  {hwpPlanFiles.map((file) => {
                                    const key = assessmentFileKey(file);
                                    return (
                                      <label
                                        key={key}
                                        className={`assessment-hwp-target-option ${selectedHwpTargetKey === key ? 'selected' : ''}`}
                                      >
                                        <input
                                          type="radio"
                                          name="assessment-hwp-target"
                                          value={key}
                                          checked={selectedHwpTargetKey === key}
                                          onChange={() => setSelectedHwpTargetKey(key)}
                                        />
                                        <span>{file.name}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </fieldset>
                            )}
                            {planFiles.length < MAX_FILES && (
                              <label className="assessment-file-add-more">
                                <input
                                  type="file"
                                  multiple
                                  accept=".pdf,.ppt,.pptx,.jpg,.jpeg,.png,.hwp,.hwpx,.docx,.xlsx,.xls"
                                  onChange={handleFileInputChange}
                                  className="assessment-file-input-hidden"
                                />
                                + 파일 추가
                              </label>
                            )}
                          </div>
                        ) : (
                          <SaenggibuAttachMenu
                            currentUser={currentUser}
                            category="assessment_plan"
                            onPick={handlePlanLibraryPick}
                            onTrackPick={() => {}}
                            onUploadClick={() => document.getElementById('assessment-plan-file-input')?.click()}
                            renderTrigger={(triggerProps) => (
                          <div
                            {...triggerProps}
                            role="button"
                            tabIndex={0}
                            className="assessment-dropzone-label"
                            data-testid="assessment-plan-dropzone"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); triggerProps.onClick(e); }
                            }}
                          >
                            <input
                              id="assessment-plan-file-input"
                              type="file"
                              multiple
                              accept=".pdf,.ppt,.pptx,.jpg,.jpeg,.png,.hwp,.hwpx,.docx,.xlsx,.xls"
                              onChange={handleFileInputChange}
                              onClick={(e) => e.stopPropagation()}
                              className="assessment-file-input-hidden"
                            />
                            <div className="assessment-dropzone-content">
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="17 8 12 3 7 8"></polyline>
                                <line x1="12" y1="3" x2="12" y2="15"></line>
                              </svg>
                              <span className="assessment-dropzone-text">파일을 그대로 올리세요</span>
                              <span className="assessment-dropzone-hint">양식에 맞출 필요 없이 PDF, HWP, PPT, 이미지, Excel (최대 {MAX_FILES}개)</span>
                            </div>
                          </div>
                            )}
                          />
                        )}
                      </div>
                    )
                  )}
                </div>

                {/* 처음 사용자 도움 */}
                {!planText.trim() && planFiles.length === 0 && planConfig.exampleData && (
                  <div className="assessment-empty-assist">
                    <details ref={assessmentAssistDetailsRef}>
                      <summary>처음이라 막막하신가요?</summary>
                      <div className="assessment-empty-assist-card">
                        <button type="button" className="assist-sheet-close" onClick={closeAssessmentAssistGuide} aria-label="도움말 닫기">닫기</button>
                        <strong>{planConfig.sectionTitle}를 그대로 넣어도 괜찮아요</strong>
                        <span>작년 문서, 성취기준, 수업 구상 메모를 복사해서 붙여넣거나 파일로 올리면 AI가 올해 형식에 맞춰 정리해요.</span>
                        <div className="assessment-empty-assist-actions">
                          <button
                            type="button"
                            onClick={() => {
                              closeAssessmentAssistGuide();
                              inputSourceRef.current = 'example';
                              setPlanText(planConfig.exampleData);
                              setInputMode('text');
                            }}
                          >
                            예시 채우기
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              closeAssessmentAssistGuide();
                              setInputMode('file');
                            }}
                          >
                            파일로 올리기
                          </button>
                        </div>
                      </div>
                    </details>
                  </div>
                )}
              </div>

              {/* Section 2: 자동 개선 안내 + 추가 변경 (순차 노출) */}
              {(planText.trim().length > 0 || planFiles.length > 0) && (
                <div className="assessment-section assessment-section-sequential">
                  <div className="assessment-auto-improvements">
                    <div className="assessment-auto-check">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                      <span>{planConfig.autoMessage}</span>
                    </div>
                  </div>

                  <div className="assessment-auto-improvements" style={{ marginBottom: 20 }}>
                    <div className="assessment-auto-check">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                      <span>학교에서 올해 특별한 지시사항이 있다면 꼭 적어주세요 <em style={{ fontStyle: 'normal', color: '#4e5968' }}>(학업성적관리위원회 결정사항, 수행평가 비율 변경 등)</em></span>
                    </div>
                  </div>

                  <div className="assessment-suggestion-area">
                    <p className="assessment-suggestion-label">
                      변경할 점이 있나요?
                    </p>
                    <div className="assessment-suggestion-chips">
                      {(planConfig.chips || []).map((chip) => {
                        const isActive = instructions.includes(chip.instruction);
                        return (
                          <button
                            key={chip.key}
                            className={`assessment-suggestion-chip ${isActive ? 'active' : ''}`}
                            onClick={() => {
                              if (isActive) {
                                setInstructions((prev) => prev.replace(chip.instruction, '').replace(/\n{2,}/g, '\n').trim());
                              } else {
                                setInstructions((prev) => (prev.trim() ? `${prev.trim()}\n${chip.instruction}` : chip.instruction));
                              }
                            }}
                          >
                            {isActive && (
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                            )}
                            {chip.label}
                          </button>
                        );
                      })}
                    </div>
                    <textarea
                      value={instructions}
                      onChange={(e) => setInstructions(e.target.value)}
                      placeholder="예: 수행평가 3개로 줄이고 비율은 40%로 해줘"
                      className="assessment-textarea assessment-textarea-instructions"
                      rows={3}
                    />
                  </div>
                </div>
              )}

              {/* 생성 모드 선택 + 만들기 버튼 */}
              <div className="assessment-submit-area">
                {/* 모드 토글 (검사기 패턴) */}
                <div className="assessment-mode-toggle-wrap">
                  <div className="assessment-mode-toggle">
                    <button
                      className={`assessment-mode-btn ${lastAnalysisMode === 'basic' ? 'active' : ''}`}
                      onClick={() => setLastAnalysisMode('basic')}
                    >
                      빠르게
                    </button>
                    <button
                      className={`assessment-mode-btn ${lastAnalysisMode === 'detailed' ? 'active' : ''}`}
                      onClick={() => {
                        if (!isPro) {
                          setShowProModal(true);
                          return;
                        }
                        setLastAnalysisMode('detailed');
                      }}
                    >
                      상세하게
                      {!isPro && <span className="assessment-mode-pro-tag">PRO</span>}
                    </button>
                  </div>
                  <p className="assessment-mode-desc">
                    {lastAnalysisMode === 'basic'
                      ? '빠르게 생성해요'
                      : '최신 교육과정을 검색해서 더 정확하고 상세하게 만들어요'}
                  </p>
                </div>

                <button
                  onClick={() => {
                    handleGenerate(lastAnalysisMode);
                  }}
                  disabled={!canGenerate || isDetecting}
                  className="assessment-submit-button"
                >
                  {isDetecting ? (
                    <span className="assessment-submit-button-loading">
                      <span className="assessment-submit-spinner" />
                      문서 분석 중...
                    </span>
                  ) : '만들기'}
                </button>
                {/* 힌트 제거 - placeholder로 충분 */}
              </div>
            </>
          )}

          {/* 여러 교과 감지 -> 교과 선택 (시트를 닫은 뒤의 대체 경로) */}
          {detectedSubjects.length > 0 && !isSubjectSheetOpen && !isLoading && !result && (
            <div className="assessment-section">
              <h2 className="assessment-section-title">
                {detectedSubjects.length}개 항목을 감지했어요
              </h2>
              <p className="assessment-subject-detect-desc">
                여러 평가 단위가 포함된 문서예요. 항목을 선택하거나 전체를 한번에 생성할 수 있어요.
              </p>
              {selectedHwpTarget && hwpPlanFiles.length > 1 && (
                <p className="assessment-subject-hwp-target" role="status">
                  한글 결과 양식: <strong>{selectedHwpTarget.name}</strong>
                </p>
              )}
              <div className="assessment-subject-cards">
                <button
                  className="assessment-subject-card assessment-subject-card-all"
                  onClick={handleGenerateAllSubjects}
                >
                  <span className="assessment-subject-card-name">전체 항목 한번에</span>
                  <span className="assessment-subject-card-grade">{detectedSubjects.length}개 항목 포함</span>
                </button>
                {detectedSubjects.map((subject) => (
                  <button
                    key={subject.id}
                    className={`assessment-subject-card ${selectedSubject?.id === subject.id ? 'selected' : ''}`}
                    onClick={() => handleSubjectSelect(subject)}
                  >
                    <span className="assessment-subject-card-name">{subject.name}</span>
                    {subject.grade && (
                      <span className="assessment-subject-card-grade">{subject.grade}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 로딩 상태 */}
          {isLoading && (
            <div className="assessment-loading">
              <div
                className="assessment-loading-progress-container"
                role="progressbar"
                aria-label="평가계획서 생성 진행률"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={loadingProgress}
              >
                <div
                  className="assessment-loading-progress-bar"
                  style={{ width: `${loadingProgress}%` }}
                />
              </div>
              <div className="assessment-loading-steps">
                {loadingSteps.map((step, i) => (
                  <div key={i} className={`assessment-loading-step ${step.done ? 'done' : i === loadingStep - 1 ? 'active' : ''}`}>
                    <span className="assessment-loading-step-icon">
                      {step.done ? '✓' : i === loadingStep - 1 ? '' : '○'}
                    </span>
                    <span>{step.label}</span>
                  </div>
                ))}
              </div>
              <p className="assessment-loading-eta">
                {/* 실측(Cloud Run 30일): generate p50 53초·p90 88초, 60초 초과 39% / generate-hwp p50 4초 */}
                {loadingHasHwp
                  ? '한글 문서까지 준비해 보통 1~2분 정도 걸려요'
                  : lastAnalysisMode === 'detailed'
                  ? '최신 자료를 확인해 보통 1~2분 정도 걸려요'
                  : '보통 1분 정도, 길면 1분 30초까지 걸려요'}
              </p>
              <p className="assessment-loading-tip">{randomTip}</p>
            </div>
          )}

          {/* 결과 영역 */}
          {result && !isLoading && (
            <div ref={resultsTopRef} className={`assessment-results ${isRefining || isHwpSyncing ? 'refining' : ''}`}>
              {(isRefining || isHwpSyncing) && (
                <div className="assessment-refine-overlay">
                  <div className="assessment-refine-overlay-content">
                    <div className="assessment-refine-spinner" />
                    <span>
                      {isHwpSyncing
                        ? '수정한 내용을 한글 문서에 반영 중...'
                        : 'AI가 수정 반영 중...'}
                    </span>
                  </div>
                </div>
              )}
              <div className="assessment-results-header">
                <h2 className="assessment-results-title">
                  {result.plan_structured?.title || planConfig.title}
                </h2>

                {/* PRO 전환 카드: Free 사용자 + basic 모드 결과일 때만 */}
                {!isPro && lastAnalysisMode === 'basic' && (
                  <div className="assessment-pro-upsell-card">
                    <div className="assessment-pro-upsell-content">
                      <p className="assessment-pro-upsell-title">이 {planConfig.label}을 더 정확하게 만들 수 있어요</p>
                      <p className="assessment-pro-upsell-desc">최신 교육과정을 실시간 검색해서 성취기준 검증 + 내용 고도화</p>
                    </div>
                    <div className="assessment-pro-upsell-actions">
                      <button className="assessment-pro-upsell-cta" onClick={() => setShowProModal(true)}>
                        상세하게 다시 만들기
                      </button>
                      <button className="assessment-pro-upsell-dismiss" onClick={(e) => { e.currentTarget.closest('.assessment-pro-upsell-card').style.display = 'none'; }}>
                        나중에
                      </button>
                    </div>
                  </div>
                )}

                {!result.hwp_result?.file_id && result.plan_structured?.overview && (
                  <div className="assessment-overview-section">
                    <button
                      className="assessment-overview-toggle"
                      onClick={() => setIsOverviewExpanded(!isOverviewExpanded)}
                    >
                      <span className="assessment-overview-toggle-label">
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3182f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                        </svg>
                        목적 / 방침
                      </span>
                      <svg
                        className={`assessment-overview-toggle-icon ${isOverviewExpanded ? 'expanded' : ''}`}
                        xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                    {isOverviewExpanded && (
                      <div className="assessment-overview-content">
                        {isOverviewEditing ? (
                          <div className="assessment-overview-edit-wrap">
                            <textarea
                              className="assessment-editable assessment-editable-full assessment-overview-textarea"
                              value={safeOverviewText(result.plan_structured.overview)}
                              onChange={(e) => {
                                const updated = {
                                  ...result,
                                  plan_structured: { ...result.plan_structured, overview: e.target.value },
                                };
                                setResult(updated);
                                resultRef.current = updated;
                                setIsSaved(false);
                              }}
                              rows={12}
                            />
                            <button
                              className="assessment-overview-edit-btn"
                              onClick={() => setIsOverviewEditing(false)}
                            >
                              수정 완료
                            </button>
                          </div>
                        ) : (
                          <div className="assessment-overview-card">
                            {safeOverviewText(result.plan_structured.overview).split('\n\n').map((section, sIdx) => {
                              const lines = section.split('\n');
                              const isHeader = lines[0]?.startsWith('[') && lines[0]?.endsWith(']');
                              return (
                                <div key={sIdx} className="assessment-overview-block">
                                  {isHeader ? (
                                    <>
                                      <h4 className="assessment-overview-block-title">{lines[0].replace(/[[\]]/g, '')}</h4>
                                      <p className="assessment-overview-block-text">{lines.slice(1).join('\n')}</p>
                                    </>
                                  ) : (
                                    <p className="assessment-overview-block-text">{section}</p>
                                  )}
                                </div>
                              );
                            })}
                            <button
                              className="assessment-overview-edit-btn"
                              onClick={() => setIsOverviewEditing(true)}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                              직접 수정하기
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {result.hwp_result?.file_id && (
                <div
                  className="assessment-result-view-panel"
                >
                  <AssessmentPlanHwpResult
                    editorRef={hwpEditorRef}
                    editorStatus={hwpEditorStatus}
                    hwpResult={result.hwp_result}
                    onDownload={(download) => {
                      trackAssessmentPlanUxEvent('hwp_download', uxBase({
                        format: download?.format,
                        ok: download?.ok === true,
                      }));
                    }}
                    onEditorError={() => setHwpEditorStatus('error')}
                    onEditorReady={() => {
                      if (hwpEditorStatus !== 'saved') setHwpEditorStatus('ready');
                    }}
                    onPotentialEdit={() => {
                      setHwpEditorStatus('potential-edit');
                      setIsSaved(false);
                    }}
                    onServerSaveStart={() => {
                      hwpWorkSaveFailedRef.current = false;
                    }}
                    onServerSave={(saveResult) => {
                      const current = resultRef.current;
                      if (!current?.hwp_result) return;
                      const savedFileId = saveResult.file_id
                        || current.hwp_result.file_id;
                      const updated = {
                        ...current,
                        hwp_result: {
                          ...current.hwp_result,
                          source_file_id: savedFileId,
                          file_id: savedFileId,
                          raw_bytes_path: `/api/chat/files/${savedFileId}/raw-bytes`,
                          server_save_path: `/api/chat/files/${savedFileId}/raw-bytes`,
                          telemetry_path: `/api/chat/files/${savedFileId}/hwp-export-telemetry`,
                          status: 'manual_saved',
                          manual_saved_at: saveResult.savedAt,
                        },
                      };
                      setResult(updated);
                      resultRef.current = updated;
                      hwpWorkSaveFailedRef.current = false;
                      setHwpEditorStatus('saved');
                      setIsSaved(false);
                      trackAssessmentPlanUxEvent('hwp_work_save', uxBase({
                        ok: true,
                        reason: saveResult.save_mode,
                      }));
                    }}
                    onServerSaveFailed={(failure) => {
                      hwpWorkSaveFailedRef.current = true;
                      setHwpEditorStatus('save-error');
                      setIsSaved(false);
                      trackAssessmentPlanUxEvent('hwp_work_save', uxBase({
                        ok: false,
                        reason: failure?.reason,
                      }));
                    }}
                  />
                </div>
              )}

              {!result.hwp_result?.file_id && result.hwp_source?.file_id && (
                <div className="assessment-hwp-fallback-card" role="status">
                  <div>
                    <strong>계획서 내용은 완성했지만 한글 양식에는 아직 반영하지 못했어요</strong>
                    <span>현재 결과를 확인하면서 원할 때 한 번 더 시도할 수 있어요.</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleRetryHwpApply}
                    disabled={isHwpSyncing}
                  >
                    {isHwpSyncing ? '다시 반영 중...' : '한글 양식 다시 반영'}
                  </button>
                </div>
              )}

              {!result.hwp_result?.file_id && result.hwp_generate_error && (
                <div className="assessment-hwp-fallback-card" role="status">
                  <div>
                    <strong>계획서 내용은 완성했지만 한글 문서를 아직 만들지 못했어요</strong>
                    <span>내용은 보관했어요. 한글 문서 만들기만 다시 시도할 수 있어요.</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleRetryHwpGenerate}
                    disabled={isHwpSyncing}
                  >
                    {isHwpSyncing ? '한글 문서 만드는 중...' : '한글 문서 다시 만들기'}
                  </button>
                </div>
              )}

              {!result.hwp_result?.file_id && (
                <div
                  className="assessment-result-view-panel"
                >
              {/* 비평가계획서 유형: 유형별 전용 결과 UI */}
              {selectedPlanType !== 'assessment' && result.plan_structured && (() => {
                const ps = result.plan_structured;
                const metaChips = [ps.subject, ps.grade, ps.semester, ps.unit, ps.lesson_number && `${ps.lesson_number}차시`, ps.total_sessions && `총 ${ps.total_sessions}차시`, ps.type, ps.project_name, ps.theme].filter(Boolean);
                const standards = ps.achievement_standards || [];
                const objectives = ps.learning_objectives || ps.objectives || [];

                return (
                  <div className="assessment-generic-result">
                    {metaChips.length > 0 && (
                      <div className="assessment-generic-meta">
                        {metaChips.map((c, i) => <span key={i} className="assessment-generic-meta-chip">{c}</span>)}
                      </div>
                    )}

                    {standards.length > 0 && (
                      <div className="assessment-generic-section">
                        <h3 className="assessment-generic-section-title">성취기준</h3>
                        <ul className="assessment-generic-list">{standards.map((s, i) => <li key={i}>{s}</li>)}</ul>
                      </div>
                    )}

                    {objectives.length > 0 && (
                      <div className="assessment-generic-section">
                        <h3 className="assessment-generic-section-title">{ps.learning_objectives ? '학습목표' : '교육목표'}</h3>
                        <ul className="assessment-generic-list">{objectives.map((o, i) => <li key={i}>{o}</li>)}</ul>
                      </div>
                    )}

                    {/* 공개수업 지도안: 도입-전개-정리 타임라인 */}
                    {selectedPlanType === 'lesson_plan' && ps.lesson_flow?.length > 0 && (
                      <div className="assessment-generic-section">
                        <h3 className="assessment-generic-section-title">수업 흐름</h3>
                        <div className="asr-timeline">
                          {ps.lesson_flow.map((step, i) => (
                            <div key={i} className={`asr-timeline-step asr-phase-${step.phase === '도입' ? 'intro' : step.phase === '전개' ? 'main' : 'wrap'}`}>
                              <div className="asr-timeline-header">
                                <span className="asr-timeline-phase">{step.phase}</span>
                                <span className="asr-timeline-duration">{step.duration}</span>
                              </div>
                              <div className="asr-timeline-body">
                                {step.activities_teacher && <div className="asr-timeline-row"><span className="asr-timeline-label">교사</span><span className="asr-timeline-text">{step.activities_teacher}</span></div>}
                                {step.activities_student && <div className="asr-timeline-row"><span className="asr-timeline-label">학생</span><span className="asr-timeline-text">{step.activities_student}</span></div>}
                                {step.materials && <div className="asr-timeline-row"><span className="asr-timeline-label">자료</span><span className="asr-timeline-text">{step.materials}</span></div>}
                                {step.notes && <div className="asr-timeline-row"><span className="asr-timeline-label">유의점</span><span className="asr-timeline-text">{step.notes}</span></div>}
                              </div>
                            </div>
                          ))}
                        </div>
                        {ps.assessment_plan && <p className="assessment-generic-text" style={{ marginTop: 12 }}><strong>평가 계획:</strong> {ps.assessment_plan}</p>}
                        {ps.board_plan && <p className="assessment-generic-text"><strong>판서 계획:</strong> {ps.board_plan}</p>}
                      </div>
                    )}

                    {/* 프로젝트 수업: GRASPS 카드 + 차시 계획 */}
                    {selectedPlanType === 'project' && ps.grasps && (
                      <div className="assessment-generic-section">
                        <h3 className="assessment-generic-section-title">GRASPS 설계</h3>
                        <div className="asr-grasps-grid">
                          {[['G', '목표', ps.grasps.goal], ['R', '역할', ps.grasps.role], ['A', '대상', ps.grasps.audience], ['S', '상황', ps.grasps.situation], ['P', '결과물', ps.grasps.product], ['S', '기준', ps.grasps.standards]].map(([letter, label, val], i) => (
                            val && <div key={i} className="asr-grasps-card"><span className="asr-grasps-letter">{letter}</span><span className="asr-grasps-label">{label}</span><p className="asr-grasps-text">{val}</p></div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedPlanType === 'project' && ps.session_plan?.length > 0 && (
                      <div className="assessment-generic-section">
                        <h3 className="assessment-generic-section-title">차시별 계획</h3>
                        <div className="assessment-generic-cards">
                          {ps.session_plan.map((s, i) => (
                            <div key={i} className="assessment-generic-card">
                              <div className="asr-session-header"><strong>{s.session}차시</strong> {s.title}</div>
                              <p className="assessment-generic-text">{s.activities}</p>
                              {s.assessment && <p className="asr-session-assess">평가: {s.assessment}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 서논술형 평가: 문항 카드 */}
                    {selectedPlanType === 'essay_eval' && ps.items?.length > 0 && (
                      <div className="assessment-generic-section">
                        <h3 className="assessment-generic-section-title">평가 문항</h3>
                        <div className="assessment-generic-cards">
                          {ps.items.map((q, i) => (
                            <div key={i} className="assessment-generic-card">
                              <div className="asr-question-header"><span className="asr-question-type">{q.question_type}</span><span className="asr-question-points">{q.points}점</span></div>
                              <p className="assessment-generic-text" style={{ fontWeight: 500 }}>{q.question}</p>
                              {q.condition && <p className="asr-question-condition">조건: {q.condition}</p>}
                              {q.scoring_criteria?.length > 0 && (
                                <div className="asr-rubric">{q.scoring_criteria.map((c, ci) => (
                                  <div key={ci} className="asr-rubric-row"><span className="asr-rubric-level">{c.level}</span><span className="asr-rubric-score">{c.score_range}</span><span className="asr-rubric-desc">{c.description}</span></div>
                                ))}</div>
                              )}
                              {q.example_answers?.length > 0 && (
                                <div className="asr-examples">{q.example_answers.map((ea, ei) => (
                                  <div key={ei} className="asr-example"><span className="asr-example-level">{ea.level}</span><p className="asr-example-text">{ea.answer}</p></div>
                                ))}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedPlanType === 'essay_eval' && ps.evaluation_elements?.length > 0 && (
                      <div className="assessment-generic-section">
                        <h3 className="assessment-generic-section-title">평가요소</h3>
                        <ul className="assessment-generic-list">{ps.evaluation_elements.map((e, i) => <li key={i}>{e}</li>)}</ul>
                      </div>
                    )}

                    {/* 탐구질문: 핵심아이디어 + 질문 카드 */}
                    {selectedPlanType === 'inquiry' && ps.core_ideas?.length > 0 && (
                      <div className="assessment-generic-section">
                        <h3 className="assessment-generic-section-title">핵심 아이디어</h3>
                        <div className="assessment-generic-cards">
                          {ps.core_ideas.map((ci, i) => (
                            <div key={i} className="assessment-generic-card">
                              <p className="assessment-generic-text"><strong>핵심 아이디어:</strong> {ci.idea}</p>
                              <p className="assessment-generic-text"><strong>핵심 문장:</strong> {ci.key_sentence}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedPlanType === 'inquiry' && ps.unit_question && (
                      <div className="asr-unit-question">
                        <span className="asr-unit-question-label">단원 핵심 질문</span>
                        <p className="asr-unit-question-text">{ps.unit_question}</p>
                      </div>
                    )}
                    {selectedPlanType === 'inquiry' && ps.inquiry_questions?.length > 0 && (
                      <div className="assessment-generic-section">
                        <h3 className="assessment-generic-section-title">탐구질문</h3>
                        <div className="assessment-generic-cards">
                          {ps.inquiry_questions.map((iq, i) => (
                            <div key={i} className="assessment-generic-card">
                              <div className="asr-iq-header"><span className={`asr-iq-type asr-iq-type--${iq.type === '사실적' ? 'fact' : iq.type === '개념적' ? 'concept' : iq.type === '논쟁적' ? 'debate' : 'transfer'}`}>{iq.type}</span></div>
                              <p className="assessment-generic-text" style={{ fontWeight: 500, fontSize: 15 }}>{iq.question}</p>
                              {iq.suggested_activities && <p className="asr-iq-activity">추천 활동: {iq.suggested_activities}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 학교자율시간: 차시 계획 */}
                    {selectedPlanType === 'autonomous' && ps.session_plan?.length > 0 && (
                      <div className="assessment-generic-section">
                        <h3 className="assessment-generic-section-title">차시별 계획</h3>
                        <div className="assessment-generic-cards">
                          {ps.session_plan.map((s, i) => (
                            <div key={i} className="assessment-generic-card">
                              <div className="asr-session-header"><strong>{s.session}차시</strong> {s.title}</div>
                              <p className="assessment-generic-text">{s.content || s.activities}</p>
                              {s.related_standards?.length > 0 && <p className="asr-session-assess">연계: {s.related_standards.join(', ')}</p>}
                              {s.assessment && <p className="asr-session-assess">평가: {s.assessment}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 공통: 루브릭/채점기준 (프로젝트, 자율시간) */}
                    {(selectedPlanType === 'project' || selectedPlanType === 'autonomous') && ps.scoring_criteria?.length > 0 && (
                      <div className="assessment-generic-section">
                        <h3 className="assessment-generic-section-title">평가기준</h3>
                        <div className="assessment-generic-cards">
                          {ps.scoring_criteria.map((c, i) => (
                            <div key={i} className="assessment-generic-card"><strong>{c.level}</strong><p className="assessment-generic-text">{c.description}</p></div>
                          ))}
                        </div>
                      </div>
                    )}

                    {ps.teaching_connection && <div className="assessment-generic-section"><h3 className="assessment-generic-section-title">수업-평가 연계</h3><p className="assessment-generic-text">{ps.teaching_connection}</p></div>}
                    {ps.notes && <div className="assessment-generic-section"><h3 className="assessment-generic-section-title">유의사항</h3><p className="assessment-generic-text">{ps.notes}</p></div>}
                  </div>
                );
              })()}

              {/* 평가계획서 전용: 배점 요약 바 + 아코디언 항목 */}
              {selectedPlanType === 'assessment' && result.plan_structured?.items?.length > 0 && (
                <>
                  <div className="assessment-points-summary">
                    <div className="assessment-points-summary-items">
                      {Object.entries(pointsSummary.byCategory).map(([category, pts]) => (
                        <span key={category} className="assessment-points-summary-chip">
                          {category} <strong>{pts}점</strong>
                        </span>
                      ))}
                      <span className="assessment-points-summary-total">
                        합계 <strong>{pointsSummary.total}점</strong>
                      </span>
                    </div>
                    <button className="assessment-toggle-all" onClick={toggleAllItems}>
                      {expandedItems.size === result.plan_structured.items.length ? '전체 접기' : '전체 펼치기'}
                    </button>
                  </div>

                  <div className="assessment-items">
                    {result.plan_structured.items.map((item, itemIdx) => {
                      const isExpanded = expandedItems.has(itemIdx);
                      return (
                        <div key={item.id || itemIdx} className={`assessment-item-card ${isExpanded ? 'expanded' : ''} ${refiningItemIndex === itemIdx ? 'refining' : ''}`}>
                          <button
                            className="assessment-item-summary"
                            onClick={() => toggleItemExpand(itemIdx)}
                          >
                            <div className="assessment-item-summary-left">
                              {item.type && (
                                <span className={`assessment-item-type-badge ${item.category === '지필평가' ? 'written' : ''}`}>
                                  {item.type}
                                </span>
                              )}
                              <span className="assessment-item-summary-name">{item.name || '항목'}</span>
                            </div>
                            <div className="assessment-item-summary-right">
                              {item.points !== undefined && (
                                <span className="assessment-item-summary-points">
                                  {item.points}점
                                  {item.ratio && <span className="assessment-item-summary-ratio">({item.ratio})</span>}
                                </span>
                              )}
                              {item.period && (
                                <span className="assessment-item-summary-period">{item.period}</span>
                              )}
                              {item.method && (
                                <span className="assessment-item-summary-method">{item.method}</span>
                              )}
                              <svg
                                className={`assessment-item-chevron ${isExpanded ? 'expanded' : ''}`}
                                xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                              >
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </div>
                          </button>

                          {isExpanded && (
                            <div className="assessment-item-body">
                              <div className="assessment-item-field-group">
                                <div className="assessment-item-field-row-top">
                                  <div className="assessment-item-field" style={{ flex: 1 }}>
                                    <span className="assessment-item-field-label">항목명</span>
                                    <input
                                      className="assessment-field-input"
                                      value={item.name || ''}
                                      onChange={(e) => handleCellEdit(itemIdx, 'name', e.target.value)}
                                    />
                                  </div>
                                  <div className="assessment-item-field">
                                    <span className="assessment-item-field-label">구분</span>
                                    <select
                                      className="assessment-field-select"
                                      value={item.category || '기타'}
                                      onChange={(e) => handleCellEdit(itemIdx, 'category', e.target.value)}
                                    >
                                      <option value="수행평가">수행평가</option>
                                      <option value="지필평가">지필평가</option>
                                      <option value="기타">기타</option>
                                    </select>
                                  </div>
                                </div>
                                <div className="assessment-item-field-row assessment-item-field-row-4">
                                  <div className="assessment-item-field">
                                    <span className="assessment-item-field-label">배점</span>
                                    <input
                                      className="assessment-field-input assessment-field-input-small"
                                      type="number"
                                      value={item.points ?? ''}
                                      onChange={(e) => handleCellEdit(itemIdx, 'points', Number(e.target.value))}
                                    />
                                  </div>
                                  <div className="assessment-item-field">
                                    <span className="assessment-item-field-label">비율</span>
                                    <input
                                      className="assessment-field-input assessment-field-input-small"
                                      value={item.ratio || ''}
                                      onChange={(e) => handleCellEdit(itemIdx, 'ratio', e.target.value)}
                                      placeholder="예: 20%"
                                    />
                                  </div>
                                  <div className="assessment-item-field">
                                    <span className="assessment-item-field-label">시기</span>
                                    <input
                                      className="assessment-field-input assessment-field-input-small"
                                      value={item.period || ''}
                                      onChange={(e) => handleCellEdit(itemIdx, 'period', e.target.value)}
                                    />
                                  </div>
                                  <div className="assessment-item-field">
                                    <span className="assessment-item-field-label">방법</span>
                                    <input
                                      className="assessment-field-input assessment-field-input-small"
                                      value={item.method || ''}
                                      onChange={(e) => handleCellEdit(itemIdx, 'method', e.target.value)}
                                    />
                                  </div>
                                </div>
                              </div>

                              {item.domain && (
                                <div className="assessment-item-field">
                                  <span className="assessment-item-field-label">평가 영역</span>
                                  <input
                                    className="assessment-field-input"
                                    value={item.domain || ''}
                                    onChange={(e) => handleCellEdit(itemIdx, 'domain', e.target.value)}
                                  />
                                </div>
                              )}

                              {item.achievement_standards && (
                                <div className="assessment-item-field">
                                  <span className="assessment-item-field-label">성취기준</span>
                                  <textarea
                                    className="assessment-field-textarea"
                                    value={typeof item.achievement_standards === 'string' ? item.achievement_standards : JSON.stringify(item.achievement_standards)}
                                    onChange={(e) => handleCellEdit(itemIdx, 'achievement_standards', e.target.value)}
                                    rows={2}
                                  />
                                </div>
                              )}

                              {item.scoring_criteria?.length > 0 && (
                                <div className="assessment-item-field">
                                  <span className="assessment-item-field-label">채점 기준</span>
                                  <div className="assessment-criteria-cards">
                                    {item.scoring_criteria.map((criteria, cIdx) => (
                                      <div key={cIdx} className="assessment-criteria-card">
                                        <div className="assessment-criteria-card-header">
                                          <input
                                            className="assessment-criteria-card-level"
                                            value={criteria.level || ''}
                                            onChange={(e) => handleScoringCriteriaEdit(itemIdx, cIdx, 'level', e.target.value)}
                                          />
                                          {criteria.score_range && (
                                            <input
                                              className="assessment-criteria-card-score"
                                              value={criteria.score_range}
                                              onChange={(e) => handleScoringCriteriaEdit(itemIdx, cIdx, 'score_range', e.target.value)}
                                            />
                                          )}
                                        </div>
                                        <textarea
                                          className="assessment-criteria-card-desc"
                                          value={criteria.description || ''}
                                          onChange={(e) => handleScoringCriteriaEdit(itemIdx, cIdx, 'description', e.target.value)}
                                          rows={2}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {item.details && (
                                <div className="assessment-item-field">
                                  <span className="assessment-item-field-label">세부사항</span>
                                  <textarea
                                    className="assessment-field-textarea"
                                    value={item.details}
                                    onChange={(e) => handleCellEdit(itemIdx, 'details', e.target.value)}
                                    rows={2}
                                  />
                                </div>
                              )}

                              {/* 항목 단위 AI 수정 */}
                              <div className="assessment-item-refine">
                                {refiningItemIndex === itemIdx ? (
                                  <div className="assessment-item-refine-loading">
                                    <div className="assessment-item-refine-progress-bar">
                                      <div className="assessment-item-refine-progress-fill" style={{ width: `${itemRefineProgress}%` }} />
                                    </div>
                                    <div className="assessment-item-refine-loading-info">
                                      <div className="assessment-item-refine-spinner" />
                                      <span className="assessment-item-refine-msg">{REFINE_LOADING_MESSAGES[refineLoadingMsgIdx]}</span>
                                    </div>
                                    <span className="assessment-item-refine-eta">약 10~20초</span>
                                  </div>
                                ) : (
                                  <div className="assessment-item-refine-row">
                                    <input
                                      className="assessment-item-refine-input"
                                      placeholder="이 항목을 어떻게 수정할까요?"
                                      value={itemRefineTexts[itemIdx] || ''}
                                      onChange={(e) => setItemRefineTexts((prev) => ({ ...prev, [itemIdx]: e.target.value }))}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                          e.preventDefault();
                                          handleItemRefine(itemIdx);
                                        }
                                      }}
                                    />
                                    <button
                                      className="assessment-item-refine-btn"
                                      disabled={!itemRefineTexts[itemIdx]?.trim()}
                                      onClick={() => handleItemRefine(itemIdx)}
                                    >
                                      AI 수정
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {result.plan_structured?.notes && (
                <div className="assessment-notes">
                  <span className="assessment-item-label">유의사항</span>
                  <textarea
                    className="assessment-editable assessment-editable-full"
                    value={result.plan_structured.notes}
                    onChange={(e) => {
                      const updated = {
                        ...result,
                        plan_structured: { ...result.plan_structured, notes: e.target.value },
                      };
                      setResult(updated);
                      resultRef.current = updated;
                      setIsSaved(false);
                    }}
                    rows={2}
                  />
                </div>
              )}
                </div>
              )}

              {curriculumGroundingNotice && (
                <p className="assessment-curriculum-grounding" role="status" data-testid="assessment-curriculum-grounding">
                  {curriculumGroundingNotice}
                </p>
              )}

              {(result.web_search_sources?.length > 0 || (
                result.analysis_mode === 'detailed'
                && result.web_search_status === 'unavailable'
              )) && (
                <div className="assessment-grounding-summary">
                  {result.web_search_sources?.length > 0 ? (
                    <SourcesButton
                      label="참고한 근거"
                      sources={result.web_search_sources}
                    />
                  ) : (
                    <p className="assessment-grounding-unavailable" role="status">
                      인터넷 근거를 불러오지 못해 입력 자료를 중심으로 만들었어요
                    </p>
                  )}
                </div>
              )}

              {/* AI 전체 수정 요청 */}
              {result.hwp_result?.file_id ? (
                <details className="assessment-hwp-ai-refine">
                  <summary>AI로 다시 수정</summary>
                  {globalRefinePanel}
                </details>
              ) : globalRefinePanel}

              {/* 비로그인 CTA */}
              {isAnonymousUser && (
                <div className="assessment-anon-cta">
                  <p className="assessment-anon-cta-title">계획서가 저장되지 않아요</p>
                  <p className="assessment-anon-cta-desc">무료 가입하면 기록을 저장하고 이어서 수정할 수 있어요</p>
                  <button
                    className="assessment-anon-cta-button"
                    onClick={() => setShowLoginModal(true)}
                  >
                    무료로 시작하기
                  </button>
                </div>
              )}

              {/* 액션 버튼 */}
              <div className="assessment-actions">
                {!isAnonymousUser && (
                  <button
                    onClick={handleSave}
                    disabled={isSaving || isSaved}
                    aria-busy={isSaving}
                    className="assessment-action-primary assessment-action-full"
                    data-testid="assessment-save-button"
                  >
                    {isSaving ? (
                      <span className="assessment-submit-button-loading">
                        <span className="assessment-submit-spinner" />
                        저장 중
                      </span>
                    ) : isSaved
                      ? '왓퀴즈 기록 저장 완료'
                      : result.hwp_result?.file_id
                      ? '왓퀴즈 기록에 저장'
                      : '저장하기'}
                  </button>
                )}
                <div className="assessment-actions-row">
                  <button onClick={handleCopy} className="assessment-action-secondary">
                    복사하기
                  </button>
                  <TeacherReferralShareButton
                    className="assessment-action-ghost"
                    surface="assessment-result"
                  />
                  {!isAnonymousUser && isSaved && (
                    <button onClick={() => navigate('/assessment-plan-history')} className="assessment-action-secondary">
                      기록 보기
                    </button>
                  )}
                  {!isAnonymousUser && (
                    <button
                      onClick={() => {
                        if (!isSaved) {
                          setShowResetConfirm(true);
                        } else {
                          setResult(null);
                          resultRef.current = null;
                          setDetectedSubjects([]);
                          setSelectedSubject(null);
                          setIsSaved(false);
                          setHwpEditorStatus('ready');
                        }
                      }}
                      className="assessment-action-ghost"
                    >
                      다시 만들기
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 모달 */}
      {showLoginModal && (
        <LoginPrompt
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          feature="assessment-plan"
        />
      )}
      {/* 여러 항목 감지 → 선택 바텀시트. 닫으면 아래 카드로 계속 고를 수 있다 */}
      <Dialog
        open={isSubjectSheetOpen && detectedSubjects.length > 0 && !isLoading && !result}
        onClose={() => setIsSubjectSheetOpen(false)}
        title="어떤 항목으로 만들까요"
        description={`${detectedSubjects.length}개 평가 단위가 들어 있어요. 필요한 항목만 골라도, 전체를 한번에 만들어도 돼요.`}
        size="md"
        testId="assessment-subject-sheet"
        footer={(
          <ModalActions
            primary={{
              label: sheetSelectedIds.length > 0
                ? `선택한 ${sheetSelectedIds.length}개로 만들기`
                : '전체 항목 한번에 만들기',
              onClick: handleSheetConfirm,
              testId: 'assessment-subject-sheet-all',
            }}
            secondary={{ label: '닫기', onClick: () => setIsSubjectSheetOpen(false) }}
          />
        )}
      >
        <div className="assessment-subject-sheet-list" role="group" aria-label="만들 항목 선택">
          {detectedSubjects.map((subject) => {
            const checked = sheetSelectedIds.includes(subject.id);
            return (
              <button
                key={subject.id}
                type="button"
                role="checkbox"
                aria-checked={checked}
                className={`assessment-subject-card assessment-subject-sheet-item ${checked ? 'selected' : ''}`}
                onClick={() => toggleSheetSubject(subject.id)}
              >
                <span className="assessment-subject-sheet-check" aria-hidden="true">
                  {checked && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </span>
                <span className="assessment-subject-card-name">{subject.name}</span>
                {subject.grade && (
                  <span className="assessment-subject-card-grade">{subject.grade}</span>
                )}
              </button>
            );
          })}
        </div>
      </Dialog>
      {showProModal && (
        <ProUpgradeModal
          isOpen={showProModal}
          onClose={() => setShowProModal(false)}
          feature={planConfig.label}
          trigger="not_pro"
        />
      )}
      {isStandardPickerOpen && (
        <AchievementStandardPicker
          isOpen={isStandardPickerOpen}
          onClose={() => setIsStandardPickerOpen(false)}
          onInsert={handleInsertStandards}
          onSchoolLevelChange={handleStandardSchoolLevelChange}
          schoolLevel={standardSchoolLevel}
        />
      )}
      <FilePreCheckModal
        isOpen={showPreCheckModal}
        onClose={() => { setShowPreCheckModal(false); setPreCheckResult(null); setPendingFiles([]); }}
        onConfirm={() => {
          setShowPreCheckModal(false);
          if (pendingFiles.length > 0) {
            setPlanFiles(prev => [...prev, ...pendingFiles]);
            setPendingFiles([]);
          }
          setPreCheckResult(null);
        }}
        checkResult={preCheckResult}
      />
      {showResetConfirm && (
        <div className="assessment-confirm-overlay" onClick={() => setShowResetConfirm(false)}>
          <div className="assessment-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <p className="assessment-confirm-title">저장하지 않은 내용이 사라져요</p>
            <p className="assessment-confirm-desc">현재 작업한 내용이 저장되지 않았어요.</p>
            <div className="assessment-confirm-actions-col">
              <button
                className="assessment-confirm-save"
                onClick={async () => {
                  setShowResetConfirm(false);
                  await handleSave();
                }}
              >
                저장하기
              </button>
              <div className="assessment-confirm-actions-row">
                <button
                  className="assessment-confirm-cancel"
                  onClick={() => setShowResetConfirm(false)}
                >
                  돌아가기
                </button>
                <button
                  className="assessment-confirm-discard"
                  onClick={() => {
                    setShowResetConfirm(false);
                    setResult(null);
                    resultRef.current = null;
                    setDetectedSubjects([]);
                    setSelectedSubject(null);
                    setIsSaved(false);
                    setHwpEditorStatus('ready');
                  }}
                >
                  저장 안 함
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {navBlocker.state === 'blocked' && (
        <div className="assessment-confirm-overlay" onClick={() => { if (!isSaving) navBlocker.reset(); }}>
          <div className="assessment-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <p className="assessment-confirm-title">
              {hasInFlightWork ? '작업을 진행하고 있어요' : '저장하지 않은 내용이 사라져요'}
            </p>
            <p className="assessment-confirm-desc">
              {hasInFlightWork
                ? '지금 나가면 진행 중인 분석이나 수정 결과를 받을 수 없어요.'
                : '현재 작업한 내용이 저장되지 않았어요.'}
            </p>
            <div className="assessment-confirm-actions-col">
              {!hasInFlightWork && (
                <button
                  className="assessment-confirm-save"
                  disabled={isSaving}
                  aria-busy={isSaving}
                  data-testid="assessment-leave-save-button"
                  onClick={async () => {
                    const saved = await handleSave();
                    if (saved) navBlocker.proceed();
                  }}
                >
                  {isSaving ? (
                    <span className="assessment-submit-button-loading">
                      <span className="assessment-submit-spinner" />
                      저장 중
                    </span>
                  ) : '저장하고 이동'}
                </button>
              )}
              <div className="assessment-confirm-actions-row">
                <button
                  className="assessment-confirm-cancel"
                  disabled={isSaving}
                  onClick={() => navBlocker.reset()}
                >
                  {hasInFlightWork ? '계속 기다리기' : '돌아가기'}
                </button>
                <button
                  className="assessment-confirm-discard"
                  disabled={isSaving}
                  onClick={() => navBlocker.proceed()}
                >
                  {hasInFlightWork ? '나가기' : '저장 안 함'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssessmentPlanPage;
